import init_wrapped from './wrapped';
import {
    uil_get_body_of_edge,
    uil_get_body_of_face,
    uil_get_body_of_vertex,
    uil_get_edges_of_face,
    uil_get_edges_of_vertex,
    uil_get_faces_of_edge,
    uil_get_vertices_of_edge,
    uil_handle_post_core_action,
    uil_handle_pre_core_action,
    uil_is_object_fixed,
} from '../ui_logic';
import { Vector3 } from 'three';
import {
    allocate_heapBytes_array,
    allocate_heapBytes_array_bundle,
    console_log,
    console_log_xml,
    free_heapBytes_array,
    get_heapbytes,
    reallocate_view_and_get_array,
    suppress_undo_tracking,
    unsuppress_undo_tracking_if_ready,
} from '../utility';
import { toArray } from '../vector3';
import {
    uid_get_ball_hardness,
    uid_get_ball_size,
    uid_reselect_all_stored_objects,
    uid_store_and_deselect_all_objects
} from '../ui_display';
import { outside } from '@vaaas/fpts/set';
import { WrappedValue } from '@phi/wrapped-value';
import { from, fromArray } from '@phi/newtype';
import { allocate_vector, getPointer, NullPointer, Pointer } from '@phi/jcore/module';
import { Body, Edge, Face, Vertex } from '@phi/jcore';

export type WrappedNumber = WrappedValue<number>;

// --------------------STATE--------------------
let uid_global_id = 1;

/** get a new, unique global id */
export function get_global_id() {
    return uid_global_id++;
}

/** resets the global id (to 1) */
export function reset_global_id() {
    uid_global_id = 1;
}

type CoreSideDraggingState =
    'move_vertices'
    | 'deform_edge'
    | 'move_vertex_on_edge'
    | 'move_edges'
    | 'face_offset'
    | 'scale_edges'
    | 'rotate_vertex_normal'
    | 'off';

let core_side_dragging_state: CoreSideDraggingState = 'off';

export const get_core_side_dragging_state = (): CoreSideDraggingState => core_side_dragging_state;

// --------------------IMPLEMENTATION--------------------

let wrapped: ReturnType<typeof init_wrapped>;

export function init(is_first_init: boolean) {
    // Initialize core
    if (!Module.ccall('phi_init', 'number', [], []))
        console.log('phi_init failed');
    if (is_first_init)
        wrapped = init_wrapped(Module);
}

export function start_move_vertices(vertex_ids: Array<Vertex>) {
    suppress_undo_tracking();
    uil_handle_pre_core_action(['start_drag']);

    for (const vid of vertex_ids) {
        user_modified_objects_hash.setItem(vid, uid_vertices.getItem(vid));
        for (const eid of uil_get_edges_of_vertex(vid)) {
            user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
            const fs = uil_get_faces_of_edge(eid);
            user_modified_objects_hash.setItem(fs[0]!, uid_faces.getItem(fs[0]!));
            user_modified_objects_hash.setItem(fs[1]!, uid_faces.getItem(fs[1]!));
        }
    }

    const heapBytes = allocate_heapBytes_array(fromArray(vertex_ids), 'int');
    const res = wrapped.start_move_vertices(vertex_ids.length, heapBytes.byteOffset);
    if (!res) debugstop();
    if (res)
        core_side_dragging_state = 'move_vertices';
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['dragging', 'start_drag']);
    return res;
}

export function move_vertices(vertex_ids: Array<Vertex>, origin: Vector3, direction: Vector3) {
    uil_handle_pre_core_action(['dragging']);
    const heapBytes = allocate_heapBytes_array(fromArray(vertex_ids), 'int');
    const res = wrapped.move_vertices(
        vertex_ids.length,
        heapBytes.byteOffset,
        origin.x,
        origin.y,
        origin.z,
        direction.x,
        direction.y,
        direction.z,
        uid_get_ball_size(),
        uid_get_ball_hardness(),
    );
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['dragging']);
    return res;
}

export function end_move_vertices(vertex_ids: Array<Vertex>) {
    uil_handle_pre_core_action(['dragging', 'end_drag']);
    const heapBytes = allocate_heapBytes_array(fromArray(vertex_ids), 'int');
    const res = wrapped.end_move_vertices(vertex_ids.length, heapBytes.byteOffset);
    if (!res) debugstop();
    if (res)
        core_side_dragging_state = 'off';
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['end_drag', 'phi_end_move_vertices']);
    unsuppress_undo_tracking_if_ready(false);
    return res;
}

export function start_deform_edge(id: Edge) {
    suppress_undo_tracking();
    uil_handle_pre_core_action(['start_drag']);
    user_modified_objects_hash.setItem(id, uid_edges.getItem(id));
    const res = wrapped.start_deform_edge(from(id));
    if (!res) debugstop();
    if (res)
        core_side_dragging_state = 'deform_edge';
    uil_handle_post_core_action(['dragging', 'start_drag']);
    return res;
}

export function deform_edge(id: Edge, origin: Vector3, direction: Vector3) {
    uil_handle_pre_core_action(['dragging']);
    const res = wrapped.deform_edge(
        from(id),
        origin.x,
        origin.y,
        origin.z,
        direction.x,
        direction.y,
        direction.z,
        uid_get_ball_size(),
        uid_get_ball_hardness()
    );
    if (!res) debugstop();
    uil_handle_post_core_action(['dragging']);
    return res;
}

export function end_deform_edge(id: Edge) {
    uil_handle_pre_core_action(['dragging', 'end_drag']);
    const res = wrapped.end_deform_edge(from(id));
    if (!res) debugstop();
    if (res)
        core_side_dragging_state = 'off';
    uil_handle_post_core_action(['end_drag', 'phi_end_deform_edge']);
    unsuppress_undo_tracking_if_ready(false);
    return res;
}

export function start_move_vertex_on_edge(vertex_id: Vertex, edge_id: Edge) {
    uil_handle_pre_core_action(['start_drag']);

    user_modified_objects_hash.setItem(vertex_id, uid_vertices.getItem(vertex_id));
    for (const eid of uil_get_edges_of_vertex(vertex_id))
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));

    const res = wrapped.start_move_vertex_on_edge(from(vertex_id), from(edge_id));
    if (!res) debugstop();
    if (res)
        core_side_dragging_state = 'move_vertex_on_edge';

    uil_handle_post_core_action(['dragging', 'start_drag']);
    return res;
}

export function move_vertex_on_edge(vertex_id: Vertex, edge_id: Edge, origin: Vector3, direction: Vector3) {
    uil_handle_pre_core_action(['dragging']);
    const res = wrapped.move_vertex_on_edge(
        from(vertex_id),
        from(edge_id),
        origin.x,
        origin.y,
        origin.z,
        direction.x,
        direction.y,
        direction.z,
        uid_get_ball_size(),
        uid_get_ball_hardness()
    );
    if (!res) debugstop();
    uil_handle_post_core_action(['dragging']);
    return res;
}

export function end_move_vertex_on_edge(vertex_id: Vertex, edge_id: Edge) {
    uil_handle_pre_core_action(['dragging', 'end_drag']);
    const res = wrapped.end_move_vertex_on_edge(from(vertex_id), from(edge_id));
    if (!res) debugstop();
    if (res)
        core_side_dragging_state = 'off';

    uil_handle_post_core_action(['end_drag']);
    return res;
}

export function start_move_edge_list(edge_ids: Array<Edge>) {
    uil_handle_pre_core_action(['start_drag']);

    for (const eid of edge_ids) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        for (const vid of uil_get_vertices_of_edge(eid))
            user_modified_objects_hash.setItem(vid, uid_vertices.getItem(vid));
    }

    const heapBytes = allocate_heapBytes_array(fromArray(edge_ids), 'int');
    const res = wrapped.start_move_edges(edge_ids.length, heapBytes.byteOffset);
    if (!res) debugstop();
    if (res)
        core_side_dragging_state = 'move_edges';
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['dragging', 'start_drag']);
    return res;
}

export function move_edge_list(
    edge_ids: Array<Edge>,
    translation_vector: Vector3,
    rotation_angle: number,
    rotation_axis_base: Vector3,
    rotation_axis_direction: Vector3,
) {
    //console_log('MOVE');
    uil_handle_pre_core_action(['dragging']);
    const heapBytes = allocate_heapBytes_array(fromArray(edge_ids), 'int');
    //uid_store_and_deselect_all_objects();
    const res = wrapped.move_edges(
        edge_ids.length,
        heapBytes.byteOffset,
        translation_vector.x,
        translation_vector.y,
        translation_vector.z,
        rotation_axis_base.x,
        rotation_axis_base.y,
        rotation_axis_base.z,
        rotation_axis_direction.x,
        rotation_axis_direction.y,
        rotation_axis_direction.z,
        rotation_angle,
    );
    if (!res) debugstop();

    free_heapBytes_array(heapBytes);
    //uid_reselect_all_stored_objects();
    uil_handle_post_core_action(['dragging']);
    return res;
}

export function end_move_edge_list(edge_ids: Array<Edge>) {
    //uil_handle_pre_core_action(null);
    uil_handle_pre_core_action(['dragging', 'end_drag']);
    const heapBytes = allocate_heapBytes_array(fromArray(edge_ids), 'int');
    uid_store_and_deselect_all_objects();
    const res = wrapped.end_move_edges(edge_ids.length, heapBytes.byteOffset);
    if (!res) debugstop();
    if (res)
        core_side_dragging_state = 'off';

    free_heapBytes_array(heapBytes);
    uid_reselect_all_stored_objects();
    uil_handle_post_core_action(['end_drag', 'phi_end_move_edges']);
    return res;
}

export function start_offset_face(face_id: Face, vertex_id: Vertex, p: Vector3) {
    uil_handle_pre_core_action(['start_drag', 'offset']);

    const faces = new Set(uid_faces.keys());
    const edges = new Set(uid_edges.keys());
    const vertices = new Set(uid_vertices.keys());

    const res = wrapped.start_face_offset(from(face_id), from(vertex_id), p.x, p.y, p.z);
    if (!res) debugstop();
    if (res) core_side_dragging_state = 'face_offset';

    uid_faces.keys()
        .filter(outside(faces))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_faces.getItem(x)));

    uid_edges.keys()
        .filter(outside(edges))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_edges.getItem(x)));

    uid_vertices.keys()
        .filter(outside(vertices))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_vertices.getItem(x)));

    uil_handle_post_core_action(['dragging', 'start_drag', 'offset']);
    return res;
}

export function offset_face_drag_step(face_id: Face, vertex_id: Vertex, p: Vector3) {
    uil_handle_pre_core_action(['dragging']);
    const res = wrapped.face_offset(from(face_id), from(vertex_id), p.x, p.y, p.z);
    if (!res) debugstop();
    uil_handle_post_core_action(['dragging']);
    return res;
}

export function end_offset_face(face_id: Face, vertex_id: Vertex) {
    uil_handle_pre_core_action(['dragging', 'end_drag']);
    const res = wrapped.end_face_offset(from(face_id), from(vertex_id));
    if (!res) debugstop();
    if (res) core_side_dragging_state = 'off';
    uil_handle_post_core_action(['end_drag', 'phi_end_face_offset']);
    return res;
}

export function start_scale_edge_list(edges: Array<Edge>) {
    uil_handle_pre_core_action(['start_drag']);

    for (const eid of edges) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        for (const vid of uil_get_vertices_of_edge(eid))
            user_modified_objects_hash.setItem(vid, uid_vertices.getItem(vid));
        for (const fid of uil_get_faces_of_edge(eid))
            user_modified_objects_hash.setItem(fid, uid_faces.getItem(fid));
    }

    const heapBytes = allocate_heapBytes_array(fromArray(edges), 'int');
    const res = wrapped.start_scale_edges(edges.length, heapBytes.byteOffset);
    if (!res) debugstop();
    if (res) core_side_dragging_state = 'scale_edges';

    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['dragging', 'start_drag']);
    return res;
}

export function scale_edge_list(
    edges: Array<Edge>,
    scale_center: Vector3,
    scale_factor: number,
    use_axis: boolean,
    axis: Vector3,
) {
    uil_handle_pre_core_action(['dragging']);
    const heapBytes = allocate_heapBytes_array(fromArray(edges), 'int');
    const res = wrapped.scale_edges(
        edges.length,
        heapBytes.byteOffset,
        scale_center.x,
        scale_center.y,
        scale_center.z,
        scale_factor,
        use_axis ? 1 : 0,
        axis.x,
        axis.y,
        axis.z,
    );
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['dragging']);
    return res;
}

export function end_scale_edge_list(edges: Array<Edge>) {
    uil_handle_pre_core_action(['dragging', 'end_drag']);
    const heapBytes = allocate_heapBytes_array(fromArray(edges), 'int');
    const res = wrapped.end_scale_edges(edges.length, heapBytes.byteOffset);
    if (!res) debugstop();
    if (res) core_side_dragging_state = 'off';
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['end_drag', 'phi_end_scale_edges']);
    return res;
}

export function listify_edges(
    input_edge_ids: Array<Edge>,
    wv_is_open_list: WrappedNumber,
    output_edge_ids: Array<Edge>,
) {
    const dummy1 = [0];
    const heapBytes_input_edge_ids_bundle = allocate_heapBytes_array_bundle(fromArray(input_edge_ids), 'int');
    const heapBytes_output_edge_ids_bundle = allocate_heapBytes_array_bundle(fromArray(input_edge_ids), 'int');
    const heapBytes_is_open_list_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_is_closed_list_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_input_edge_ids = get_heapbytes(heapBytes_input_edge_ids_bundle);
    const heapBytes_output_edge_ids = get_heapbytes(heapBytes_output_edge_ids_bundle);
    const heapBytes_is_open_list = get_heapbytes(heapBytes_is_open_list_bundle);
    const heapBytes_is_closed_list = get_heapbytes(heapBytes_is_closed_list_bundle);

    const res = wrapped.listify_edges(
        input_edge_ids.length,
        heapBytes_input_edge_ids.byteOffset,
        heapBytes_is_open_list.byteOffset,
        heapBytes_is_closed_list.byteOffset,
        heapBytes_output_edge_ids.byteOffset
    );
    if (!res) debugstop();

    let v_is_closed_list = undefined;

    if (res) {
        const heapInts_output_edge_ids = reallocate_view_and_get_array(heapBytes_output_edge_ids_bundle, 'Int32Array', input_edge_ids.length);
        const heapInts_is_open_list = reallocate_view_and_get_array(heapBytes_is_open_list_bundle, 'Int32Array', 1);
        const heapInts_is_closed_list = reallocate_view_and_get_array(heapBytes_is_closed_list_bundle, 'Int32Array', 1);
        for (let i = 0; i < input_edge_ids.length; i++)
            output_edge_ids.push(heapInts_output_edge_ids[i] as any as Edge);
        wv_is_open_list.v = heapInts_is_open_list[0]!;
        v_is_closed_list = heapInts_is_closed_list[0];
    }

    free_heapBytes_array(heapBytes_input_edge_ids);
    free_heapBytes_array(heapBytes_output_edge_ids);
    free_heapBytes_array(heapBytes_is_open_list);
    free_heapBytes_array(heapBytes_is_closed_list);

    return Boolean(res && (wv_is_open_list.v || v_is_closed_list));
}

export function get_face_connected_component_info(
    input_face_ids: Array<Face>,
    input_edge_ids: Array<Edge>,
    input_vertex_ids: Array<Vertex>,
    wv_is_one_cc: WrappedValue<boolean>,
    output_boundary_edge_ids: Array<Edge>,
    output_boundary_vertex_ids: Array<Vertex>,
) {
    const dummy1 = [0];
    // Output arrays will be given enough size to fit all input edges (worst case)
    const heapBytes_input_face_ids_bundle = allocate_heapBytes_array_bundle(fromArray(input_face_ids), 'int');
    const heapBytes_output_edge_ids_bundle = allocate_heapBytes_array_bundle(fromArray(input_edge_ids), 'int');
    const heapBytes_output_vertex_ids_bundle = allocate_heapBytes_array_bundle(fromArray(input_vertex_ids), 'int');
    const heapBytes_is_one_cc_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_is_mult_cc_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_nbedges_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_nbvertices_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_input_face_ids = get_heapbytes(heapBytes_input_face_ids_bundle);
    const heapBytes_output_edge_ids = get_heapbytes(heapBytes_output_edge_ids_bundle);
    const heapBytes_output_vertex_ids = get_heapbytes(heapBytes_output_vertex_ids_bundle);
    const heapBytes_is_one_cc = get_heapbytes(heapBytes_is_one_cc_bundle);
    const heapBytes_is_mult_cc = get_heapbytes(heapBytes_is_mult_cc_bundle);
    const heapBytes_nbedges = get_heapbytes(heapBytes_nbedges_bundle);
    const heapBytes_nbvertices = get_heapbytes(heapBytes_nbvertices_bundle);

    const res = wrapped.get_face_connected_component_info(
        input_face_ids.length,
        heapBytes_input_face_ids.byteOffset,
        heapBytes_is_one_cc.byteOffset,
        heapBytes_is_mult_cc.byteOffset,
        heapBytes_nbedges.byteOffset,
        heapBytes_output_edge_ids.byteOffset,
        heapBytes_nbvertices.byteOffset,
        heapBytes_output_vertex_ids.byteOffset,
    );

    if (!res) debugstop();
    if (res) {
        const heapInts_is_one_cc = reallocate_view_and_get_array(heapBytes_is_one_cc_bundle, 'Int32Array', 1);
        const heapInts_is_mult_cc = reallocate_view_and_get_array(heapBytes_is_mult_cc_bundle, 'Int32Array', 1);
        const heapInts_nbedges = reallocate_view_and_get_array(heapBytes_nbedges_bundle, 'Int32Array', 1);
        const heapInts_nbvertices = reallocate_view_and_get_array(heapBytes_nbvertices_bundle, 'Int32Array', 1);
        wv_is_one_cc.v = heapInts_is_one_cc[0] ? true : false;
        const nbedges = heapInts_nbedges[0]!;
        const nbvertices = heapInts_nbvertices[0]!;
        const heapInts_output_edge_ids = reallocate_view_and_get_array(heapBytes_output_edge_ids_bundle, 'Int32Array', nbedges);
        const heapInts_output_vertex_ids = reallocate_view_and_get_array(heapBytes_output_vertex_ids_bundle, 'Int32Array', nbvertices);

        for (let i = 0; i < nbedges; i++)
            output_boundary_edge_ids.push(heapInts_output_edge_ids[i] as any as Edge);

        for (let i = 0; i < nbvertices; i++)
            output_boundary_vertex_ids.push(heapInts_output_vertex_ids[i] as any as Vertex);
    }

    free_heapBytes_array(heapBytes_input_face_ids);
    free_heapBytes_array(heapBytes_output_edge_ids);
    free_heapBytes_array(heapBytes_output_vertex_ids);
    free_heapBytes_array(heapBytes_is_one_cc);
    free_heapBytes_array(heapBytes_is_mult_cc);
    free_heapBytes_array(heapBytes_nbedges);
    free_heapBytes_array(heapBytes_nbvertices);

    return res;
}

export function get_face_connected_components(
    input_face_ids: Array<Face>,
    output_connected_component_array: Array<Array<Face>>,
) {
    // Output arrays will be given enough size to fit all input edges (worst case)
    const dummy1 = [0];
    const heapBytes_input_face_ids_bundle = allocate_heapBytes_array_bundle(fromArray(input_face_ids), 'int');
    const heapBytes_ncc_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_cc_count_bundle = allocate_heapBytes_array_bundle(fromArray(input_face_ids), 'int');
    const heapBytes_cc_ids_bundle = allocate_heapBytes_array_bundle(fromArray(input_face_ids), 'int');
    const heapBytes_input_face_ids = get_heapbytes(heapBytes_input_face_ids_bundle);
    const heapBytes_ncc = get_heapbytes(heapBytes_ncc_bundle);
    const heapBytes_cc_count = get_heapbytes(heapBytes_cc_count_bundle);
    const heapBytes_cc_ids = get_heapbytes(heapBytes_cc_ids_bundle);


    const res = wrapped.get_face_connected_components(
        input_face_ids.length,
        heapBytes_input_face_ids.byteOffset,
        heapBytes_ncc.byteOffset,
        heapBytes_cc_count.byteOffset,
        heapBytes_cc_ids.byteOffset
    );
    if (!res) {debugstop();}

    if (res) {
        const heapInts_ncc = reallocate_view_and_get_array(heapBytes_ncc_bundle, 'Int32Array', 1);
        const ncc = heapInts_ncc[0]!;
        const heapInts_cc_count = reallocate_view_and_get_array(heapBytes_cc_count_bundle, 'Int32Array', ncc);
        const heapInts_cc_ids = reallocate_view_and_get_array(heapBytes_cc_ids_bundle, 'Int32Array', input_face_ids.length);

        let index = 0;
        for (let i = 0; i < ncc; i++) {
            const arr: Array<Face> = [];
            for (let j = 0; j < heapInts_cc_count[i]!; j ++) {
                arr.push(heapInts_cc_ids[index] as any as Face);
                index++;
            }
            output_connected_component_array.push(arr);
        }
    }

    free_heapBytes_array(heapBytes_input_face_ids);
    free_heapBytes_array(heapBytes_ncc);
    free_heapBytes_array(heapBytes_cc_count);
    free_heapBytes_array(heapBytes_cc_ids);

    return res;
}

export function get_edge_list_geometry(
    edge_ids: Array<Edge>,
    is_high_res: boolean,
    centre_coords: Vector3,
    norm_coords: Vector3,
    wv_radius: WrappedNumber,
    characteristic_dir_coords: Vector3,
) {
    const dummy3 = [0, 0, 0];
    const dummy1 = [0];
    const heapBytes_edge_ids_bundle = allocate_heapBytes_array_bundle(fromArray(edge_ids), 'int');
    const heapBytes_center_coords_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_norm_coords_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_radius_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_characteristic_dir_coords_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_edge_ids = get_heapbytes(heapBytes_edge_ids_bundle);
    const heapBytes_center_coords = get_heapbytes(heapBytes_center_coords_bundle);
    const heapBytes_norm_coords = get_heapbytes(heapBytes_norm_coords_bundle);
    const heapBytes_radius = get_heapbytes(heapBytes_radius_bundle);
    const heapBytes_characteristic_dir_coords = get_heapbytes(heapBytes_characteristic_dir_coords_bundle);

    const res = wrapped.get_geometry_of_edges(
        edge_ids.length,
        getPointer(heapBytes_edge_ids),
        is_high_res ? 1 : 0,
        getPointer(heapBytes_center_coords),
        getPointer(heapBytes_norm_coords),
        getPointer(heapBytes_radius),
        getPointer(heapBytes_characteristic_dir_coords),
    );
    if (!res) debugstop();

    if (res) {
        const heapFloats_center_coords = reallocate_view_and_get_array(heapBytes_center_coords_bundle, 'Float32Array', 3);
        const heapFloats_norm_coords = reallocate_view_and_get_array(heapBytes_norm_coords_bundle, 'Float32Array', 3);
        const heapFloats_radius = reallocate_view_and_get_array(heapBytes_radius_bundle, 'Float32Array', 1);
        const heapFloats_characteristic_dir_coords = reallocate_view_and_get_array(heapBytes_characteristic_dir_coords_bundle, 'Float32Array', 3);

        // Copy results to JS structures
        centre_coords.x = heapFloats_center_coords[0]!;
        centre_coords.y = heapFloats_center_coords[1]!;
        centre_coords.z = heapFloats_center_coords[2]!;
        norm_coords.x = heapFloats_norm_coords[0]!;
        norm_coords.y = heapFloats_norm_coords[1]!;
        norm_coords.z = heapFloats_norm_coords[2]!;
        wv_radius.v = heapFloats_radius[0]!;
        characteristic_dir_coords.x = heapFloats_characteristic_dir_coords[0]!;
        characteristic_dir_coords.y = heapFloats_characteristic_dir_coords[1]!;
        characteristic_dir_coords.z = heapFloats_characteristic_dir_coords[2]!;
    }

    free_heapBytes_array(heapBytes_center_coords);
    free_heapBytes_array(heapBytes_norm_coords);
    free_heapBytes_array(heapBytes_radius);
    free_heapBytes_array(heapBytes_characteristic_dir_coords);
    return res;
}

export function get_face_geometry(
    face_id: Face,
    centre_coords: Vector3,
    norm_coords: Vector3,
    wv_radius: WrappedNumber,
    characteristic_dir_coords: Vector3,
    wv_flatness: WrappedNumber,
) {
    const dummy3 = [0, 0, 0];
    const dummy1 = [0];
    const heapBytes_center_coords_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_norm_coords_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_radius_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_characteristic_dir_coords_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_flatness_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_center_coords = get_heapbytes(heapBytes_center_coords_bundle);
    const heapBytes_norm_coords = get_heapbytes(heapBytes_norm_coords_bundle);
    const heapBytes_radius = get_heapbytes(heapBytes_radius_bundle);
    const heapBytes_characteristic_dir_coords = get_heapbytes(heapBytes_characteristic_dir_coords_bundle);
    const heapBytes_flatness = get_heapbytes(heapBytes_flatness_bundle);

    const res = wrapped.get_face_geometry(
        face_id,
        getPointer(heapBytes_center_coords),
        getPointer(heapBytes_norm_coords),
        getPointer(heapBytes_radius),
        getPointer(heapBytes_characteristic_dir_coords),
        getPointer(heapBytes_flatness),
    );
    if (!res) debugstop();

    if (res) {
        const heapFloats_center_coords = reallocate_view_and_get_array(heapBytes_center_coords_bundle, 'Float32Array', 3);
        const heapFloats_norm_coords = reallocate_view_and_get_array(heapBytes_norm_coords_bundle, 'Float32Array', 3);
        const heapFloats_radius = reallocate_view_and_get_array(heapBytes_radius_bundle, 'Float32Array', 1);
        const heapFloats_characteristic_dir_coords = reallocate_view_and_get_array(heapBytes_characteristic_dir_coords_bundle, 'Float32Array', 3);
        const heapFloats_flatness = reallocate_view_and_get_array(heapBytes_flatness_bundle, 'Float32Array', 1);

        centre_coords.x = heapFloats_center_coords[0]!;
        centre_coords.y = heapFloats_center_coords[1]!;
        centre_coords.z = heapFloats_center_coords[2]!;
        norm_coords.x = heapFloats_norm_coords[0]!;
        norm_coords.y = heapFloats_norm_coords[1]!;
        norm_coords.z = heapFloats_norm_coords[2]!;
        wv_radius.v = heapFloats_radius[0]!;
        characteristic_dir_coords.x = heapFloats_characteristic_dir_coords[0]!;
        characteristic_dir_coords.y = heapFloats_characteristic_dir_coords[1]!;
        characteristic_dir_coords.z = heapFloats_characteristic_dir_coords[2]!;
        wv_flatness.v = heapFloats_flatness[0]!;
    }

    free_heapBytes_array(heapBytes_center_coords);
    free_heapBytes_array(heapBytes_norm_coords);
    free_heapBytes_array(heapBytes_radius);
    free_heapBytes_array(heapBytes_characteristic_dir_coords);
    free_heapBytes_array(heapBytes_flatness);

    return res;
}

export function get_sedge_num_of_face(face_id: Face) {
    let nse = -1;
    const dummy1 = [0];
    const heapBytes_nse_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_nse = get_heapbytes(heapBytes_nse_bundle);

    const res = wrapped.get_sedge_num_of_face(from(face_id), heapBytes_nse.byteOffset);
    if (!res) debugstop();

    if (res) {
        const heapInts_nse = reallocate_view_and_get_array(heapBytes_nse_bundle, 'Int32Array', 1);
        nse = heapInts_nse[0]!;
    }
    free_heapBytes_array(heapBytes_nse);
    return nse;
}

export function get_face_superedges(face_id: Face, ne: number, nse: number) {
    const sedges_alloc = new Array(nse*2+ne);
    const heapBytes_sedges_bundle = allocate_heapBytes_array_bundle(sedges_alloc, 'int');
    const heapBytes_sedges = get_heapbytes(heapBytes_sedges_bundle);

    const res = wrapped.get_sedges_of_face(
        face_id,
        getPointer(heapBytes_sedges),
    );
    if (!res) debugstop();

    let sedge_ids = null;
    if (res)  {
        sedge_ids = [] as Array<number>;
        const heapInts_sedges = reallocate_view_and_get_array(heapBytes_sedges_bundle, 'Int32Array', sedges_alloc.length);
        for (let i = 0; i < nse*2+ne; i++)
            sedge_ids.push(heapInts_sedges[i]!);
    }
    free_heapBytes_array(heapBytes_sedges);
    return sedge_ids;
}

export function get_face_crosshair_at_params(
    face_id: Face,
    u: number,
    v: number,
    edge_ids: Array<Edge>,
    coords: Array<Vector3>,
) {
    const coords_alloc = new Array(12);
    const eids_alloc = new Array(12);
    const heapBytes_coords_bundle = allocate_heapBytes_array_bundle(coords_alloc, 'float');
    const heapBytes_eids_bundle = allocate_heapBytes_array_bundle(eids_alloc, 'int');
    const heapBytes_coords = get_heapbytes(heapBytes_coords_bundle);
    const heapBytes_eids = get_heapbytes(heapBytes_eids_bundle);

    const res = wrapped.get_face_crosshair_at_params(
        from(face_id),
        u,
        v,
        heapBytes_coords.byteOffset,
        heapBytes_eids.byteOffset,
    );
    if (!res) debugstop();

    if (res) {
        const heapFloats_coords = reallocate_view_and_get_array(heapBytes_coords_bundle, 'Float32Array', coords_alloc.length);
        const heapInts_eids = reallocate_view_and_get_array(heapBytes_eids_bundle, 'Int32Array', eids_alloc.length);
        const p1 = new Vector3(heapFloats_coords[0], heapFloats_coords[1], heapFloats_coords[2]);
        const p2 = new Vector3(heapFloats_coords[3], heapFloats_coords[4], heapFloats_coords[5]);
        const p3 = new Vector3(heapFloats_coords[6], heapFloats_coords[7], heapFloats_coords[8]);
        const p4 = new Vector3(heapFloats_coords[9], heapFloats_coords[10], heapFloats_coords[11]);
        const e1 = heapInts_eids[0] as any as Edge;
        const e2 = heapInts_eids[1] as any as Edge;
        const e3 = heapInts_eids[2] as any as Edge;
        const e4 = heapInts_eids[3] as any as Edge;
        coords.push(p1);
        coords.push(p2);
        coords.push(p3);
        coords.push(p4);
        edge_ids.push(e1);
        edge_ids.push(e2);
        edge_ids.push(e3);
        edge_ids.push(e4);
    }

    free_heapBytes_array(heapBytes_coords);
    free_heapBytes_array(heapBytes_eids);
    return res;
}

export function get_ray_edge_intersection(
    eid: Edge,
    ray_base: Vector3,
    ray_direction: Vector3,
    is_high_res: boolean,
    wv_parameter: WrappedNumber,
) {
    const dummy1 = [0];
    const dummy3 = [0, 0, 0];
    const heapBytes_intersection_point_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_intersection_point = get_heapbytes(heapBytes_intersection_point_bundle);
    const heapBytes_parameter_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_parameter = get_heapbytes(heapBytes_parameter_bundle);

    const res = wrapped.get_ray_edge_intersection(
        from(eid),
        ray_base.x,
        ray_base.y,
        ray_base.z,
        ray_direction.x,
        ray_direction.y,
        ray_direction.z,
        is_high_res ? 1 : 0,
        heapBytes_intersection_point.byteOffset,
        heapBytes_parameter.byteOffset,
    );
    if (!res) debugstop();

    let intersection_point = null as null | Vector3;
    if (res) {
        const heapFloats_intersection_point = reallocate_view_and_get_array(heapBytes_intersection_point_bundle, 'Float32Array', 3);
        const heapFloats_parameter = reallocate_view_and_get_array(heapBytes_parameter_bundle, 'Float32Array', 1);

        intersection_point = new Vector3();
        intersection_point.x = heapFloats_intersection_point[0]!;
        intersection_point.y = heapFloats_intersection_point[1]!;
        intersection_point.z = heapFloats_intersection_point[2]!;
        wv_parameter.v = heapFloats_parameter[0]!;
    }

    free_heapBytes_array(heapBytes_intersection_point);
    free_heapBytes_array(heapBytes_parameter);
    return intersection_point;
}

export function get_face_ray_intersection(
    fid: Face,
    ray_base: Vector3,
    ray_direction: Vector3,
    wv_u: WrappedNumber,
    wv_v: WrappedNumber,
) {
    const dummy1 = [0];
    const dummy3 = [0, 0, 0];

    const heapBytes_intersection_point_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_intersection_point = get_heapbytes(heapBytes_intersection_point_bundle);
    const heapBytes_u_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_u = get_heapbytes(heapBytes_u_bundle);
    const heapBytes_v_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_v = get_heapbytes(heapBytes_v_bundle);

    const res = wrapped.get_face_ray_intersection(
        from(fid),
        ray_base.x,
        ray_base.y,
        ray_base.z,
        ray_direction.x,
        ray_direction.y,
        ray_direction.z,
        heapBytes_intersection_point.byteOffset,
        heapBytes_u.byteOffset,
        heapBytes_v.byteOffset
    );
    //if (!res) {debugstop();}

    let intersection_point = null as null | Vector3;
    if (res) {
        const heapFloats_intersection_point = reallocate_view_and_get_array(heapBytes_intersection_point_bundle, 'Float32Array', 3);
        const heapFloats_u = reallocate_view_and_get_array(heapBytes_u_bundle, 'Float32Array', 1);
        const heapFloats_v = reallocate_view_and_get_array(heapBytes_v_bundle, 'Float32Array', 1);

        intersection_point = new Vector3();
        intersection_point.x = heapFloats_intersection_point[0]!;
        intersection_point.y = heapFloats_intersection_point[1]!;
        intersection_point.z = heapFloats_intersection_point[2]!;
        wv_u.v = heapFloats_u[0]!;
        wv_v.v = heapFloats_v[0]!;
    }

    free_heapBytes_array(heapBytes_intersection_point);
    free_heapBytes_array(heapBytes_u);
    free_heapBytes_array(heapBytes_v);

    return intersection_point;
}

export function get_face_normal_at_edge_point(
    fid: Face,
    eid: Edge,
    ray_base: Vector3,
    ray_direction: Vector3,
) {
    const dummy1 = [0];
    const heapBytes_normal_x_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_normal_x = get_heapbytes(heapBytes_normal_x_bundle);
    const heapBytes_normal_y_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_normal_y = get_heapbytes(heapBytes_normal_y_bundle);
    const heapBytes_normal_z_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_normal_z = get_heapbytes(heapBytes_normal_z_bundle);

    const res = wrapped.get_face_norm_at_edge_pt(
        from(fid),
        from(eid),
        ray_base.x,
        ray_base.y,
        ray_base.z,
        ray_direction.x,
        ray_direction.y,
        ray_direction.z,
        heapBytes_normal_x.byteOffset,
        heapBytes_normal_y.byteOffset,
        heapBytes_normal_z.byteOffset,
    );
    if (!res) debugstop();

    let normal = null as null | Vector3;
    if (res) {
        const heapFloats_normal_x = reallocate_view_and_get_array(heapBytes_normal_x_bundle, 'Float32Array', 1);
        const heapFloats_normal_y = reallocate_view_and_get_array(heapBytes_normal_y_bundle, 'Float32Array', 1);
        const heapFloats_normal_z = reallocate_view_and_get_array(heapBytes_normal_z_bundle, 'Float32Array', 1);

        normal = new Vector3();
        normal.x = heapFloats_normal_x[0]!;
        normal.y = heapFloats_normal_y[0]!;
        normal.z = heapFloats_normal_z[0]!;
    }

    free_heapBytes_array(heapBytes_normal_x);
    free_heapBytes_array(heapBytes_normal_y);
    free_heapBytes_array(heapBytes_normal_z);

    return normal;
}


export function start_rotate_vertex_normal(id: Vertex) {
    uil_handle_pre_core_action(['start_drag']);

    user_modified_objects_hash.setItem(id, uid_vertices.getItem(id));
    for (const eid of uil_get_edges_of_vertex(id)) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
    }
    const res = wrapped.start_rotate_vertex_normal(from(id));
    if (!res) debugstop();
    if (res) core_side_dragging_state = 'rotate_vertex_normal';

    uil_handle_post_core_action(['dragging', 'start_drag']);
    return res;
}

export function rotate_vertex_normal(id: Vertex, direction: Vector3, angle: number) {
    uil_handle_pre_core_action(['dragging']);
    const res = wrapped.rotate_vertex_normal(from(id), direction.x, direction.y, direction.z, angle);
    if (!res) debugstop();
    uil_handle_post_core_action(['dragging']);
    return res;
}

export function end_rotate_vertex_normal(id: Vertex) {
    uil_handle_pre_core_action(['dragging', 'end_drag']);
    const res = wrapped.end_rotate_vertex_normal(from(id));
    if (!res) {debugstop();}
    if (res) core_side_dragging_state = 'off';
    uil_handle_post_core_action(['end_drag', 'phi_end_rotate_vertex_normal']);
    return res;
}

export function start_rotate_edge_tangent(eid: Edge, vid: Vertex) {
    uil_handle_pre_core_action(['start_drag']);

    user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
    const fs = uil_get_faces_of_edge(eid);
    user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
    user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));

    const res = wrapped.start_rotate_vertex_normal(from(vid)); //XXX EDGETANGENT
    if (!res) debugstop();
    if (res) core_side_dragging_state = 'rotate_vertex_normal';

    uil_handle_post_core_action(['dragging', 'start_drag']);
    return res;
}

export function rotate_edge_tangent(eid: Edge, vid: Vertex, direction: Vector3, angle: number) {
    uil_handle_pre_core_action(['dragging']);
    const res = wrapped.rotate_vertex_normal(from(vid), direction.x, direction.y, direction.z, angle); // EDGETANGENT
    if (!res) debugstop();
    uil_handle_post_core_action(['dragging']);
    return res;
}

export function end_rotate_edge_tangent(eid: Edge, vid: Vertex) {
    uil_handle_pre_core_action(['dragging', 'end_drag']);
    const res = wrapped.end_rotate_vertex_normal(from(vid)); // XXX EDGETANGENT
    if (!res) debugstop();
    if (res) core_side_dragging_state = 'off';
    uil_handle_post_core_action(['end_drag']);
    return res;
}

export function delete_body(body_id: Body) {
    uil_handle_pre_core_action(null);
    const res = wrapped.delete_body(from(body_id));
    if (!res) debugstop();
    uil_handle_post_core_action(null);
    return res;
}

export function delete_objects(object_ids: Array<Vertex | Edge | Face>) {
    uil_handle_pre_core_action(null);
    const heapBytes = allocate_heapBytes_array(fromArray(object_ids), 'int');
    const res = wrapped.delete_objects(object_ids.length, heapBytes.byteOffset);
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['delete_objects']);
    return res;
}

export function delete_faces(object_ids: Array<Face>) {
    // phi_delete_faces returns number of new faces created (0 for failure).
    // Also places in first_new_face_id the id of the first of the new face(s) created, or -1 if none (failure).
    uil_handle_pre_core_action(null);
    const dummy = [0];
    const heapBytes_first_new_face_id_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes_first_new_face_id = get_heapbytes(heapBytes_first_new_face_id_bundle);
    const heapBytes = allocate_heapBytes_array(fromArray(object_ids), 'int');
    const res: number = wrapped.delete_faces(
        object_ids.length,
        getPointer(heapBytes),
        getPointer(heapBytes_first_new_face_id),
    );
    if (res === 0) {debugstop();}
    let return_value = 0;
    if (res > 0) {
        const heapInts_first_new_face_id = reallocate_view_and_get_array(heapBytes_first_new_face_id_bundle, 'Int32Array', 1);
        return_value = heapInts_first_new_face_id[0]!;
    }
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['delete_objects']);
    return return_value;
}

export function delete_vertex(vertex_id: Vertex, no_geometry_change = false) {
    uil_handle_pre_core_action(null);
    uid_current_api_call = 'phi_delete_vertex';
    const res = wrapped.delete_vertex(from(vertex_id), no_geometry_change ? 1 : 0);
    if (!res) debugstop();
    uid_current_api_call = 'null';
    uil_handle_post_core_action(['delete_objects']);
    return res;
}

export function add_vertex(edge_id: Edge, ray_base: Vector3, ray_direction: Vector3) {
    uil_handle_pre_core_action(['phi_add_vertex']);
    const id = wrapped.add_vertex(
        from(edge_id),
        ray_base.x,
        ray_base.y,
        ray_base.z,
        ray_direction.x,
        ray_direction.y,
        ray_direction.z,
    ) as Vertex | 0;
    if (!id) debugstop();
    if (id) {
        user_modified_objects_hash.setItem(id, uid_vertices.getItem(id));
        for (const eid of uil_get_edges_of_vertex(id))
            user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
    }
    uil_handle_post_core_action(['phi_add_vertex']);
    return id;
}

export function add_vertex_at_param(edge_id: Edge, t: number) {
    uil_handle_pre_core_action(['phi_add_vertex']);
    const id = wrapped.add_vertex_at_param(from(edge_id), t) as Vertex | 0;
    if (!id) debugstop();
    if (id) {
        user_modified_objects_hash.setItem(id, uid_vertices.getItem(id));
        for (const eid of uil_get_edges_of_vertex(id))
            user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
    }
    uil_handle_post_core_action(['phi_add_vertex']);
    return id;
}

export function add_edge(
    face_id: Face,
    vertex1_id: Vertex,
    vertex2_id: Vertex,
    sample_points: Vector3[],
    sample_normals: Vector3[],
    is_planar: boolean,
) {
    uil_handle_pre_core_action(['phi_add_edge']);
    const sample_coords = sample_points.flatMap(toArray);
    const sample_normal_coords = sample_normals.flatMap(toArray);

    const heapBytes_points = allocate_heapBytes_array(sample_coords, 'float');
    const heapBytes_normals = allocate_heapBytes_array(sample_normal_coords, 'float');
    //id = wrapped.add_edge(face_id, vertex1_id, vertex2_id, sample_points.length, heapBytes_points.byteOffset, heapBytes_normals.byteOffset, is_planar);
    const id = wrapped.add_edge(
        from(face_id),
        from(vertex1_id),
        from(vertex2_id),
        sample_points.length,
        heapBytes_points.byteOffset,
        is_planar ? 1 : 0,
    ) as Edge | 0;
    if (!id) debugstop();

    free_heapBytes_array(heapBytes_points);
    free_heapBytes_array(heapBytes_normals);

    if (id) {
        user_modified_objects_hash.setItem(id, uid_edges.getItem(id));
        const fs = uil_get_faces_of_edge(id);
        user_modified_objects_hash.setItem(fs[0]!, uid_faces.getItem(fs[0]!));
        user_modified_objects_hash.setItem(fs[1]!, uid_faces.getItem(fs[1]!));
    }

    uil_handle_post_core_action(['phi_add_edge']);
    return id;
}

export function modify_edge_geometry(
    edge_id: Edge,
    vertex1_id: Vertex,
    vertex2_id: Vertex,
    samples: Array<Vector3>,
) {
    uil_handle_pre_core_action(['phi_modify_edge_geometry']);
    const sample_coords = samples.flatMap(toArray);
    const heapBytes = allocate_heapBytes_array(sample_coords, 'float');
    const res = wrapped.modify_edge_geometry(from(edge_id), from(vertex1_id), from(vertex2_id), samples.length, heapBytes.byteOffset);
    if (!res) debugstop();

    free_heapBytes_array(heapBytes);

    if (res) {
        user_modified_objects_hash.setItem(edge_id, uid_edges.getItem(edge_id));
        const fs = uil_get_faces_of_edge(edge_id);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
    }

    uil_handle_post_core_action(['phi_modify_edge_geometry']);
    return res;
}

export function find_edge_shared_vertex(e1id: Edge, e2id: Edge) {
    const id = wrapped.find_edge_shared_vertex(from(e1id), from(e2id)) as any as Vertex;
    //if (!id) {debugstop();}
    return id;
}

export function add_joint_smoothness(
    e1id: Edge,
    e2id: Edge,
    strength: number,
    propagate_to_symmetry: boolean,
) {
    strength = Math.max(strength, 0.01);

    uil_handle_pre_core_action(['phi_add_joint_smoothness']);

    user_modified_objects_hash.setItem(e1id, uid_edges.getItem(e1id));
    user_modified_objects_hash.setItem(e2id, uid_edges.getItem(e2id));

    // We ignore the radius and pass 0
    const res = wrapped.add_joint_smoothness(from(e1id), from(e2id), strength);
    if (!res) {debugstop();}

    uil_handle_post_core_action(['phi_add_joint_smoothness', propagate_to_symmetry ? null : 'dont_propagate_to_symmetry']);
    return res;
}

export function remove_joint_smoothness(
    e1id: Edge,
    e2id: Edge,
    no_geometry_change = false,
    propagate_to_symmetry: boolean,
) {
    uil_handle_pre_core_action(['phi_remove_joint_smoothness']);

    user_modified_objects_hash.setItem(e1id, uid_edges.getItem(e1id));
    user_modified_objects_hash.setItem(e2id, uid_edges.getItem(e2id));

    // We ignore the radius and pass 0
    const res = wrapped.remove_joint_smoothness(from(e1id), from(e2id), no_geometry_change ? 1 : 0);
    if (!res) {debugstop();}

    uil_handle_post_core_action(['phi_remove_joint_smoothness', propagate_to_symmetry ? null : 'dont_propagate_to_symmetry']);
    return res;
}

export function can_make_edge_smooth(eid: Edge) {
    const res = wrapped.can_make_edge_smooth(from(eid));
    if (res < 0) debugstop();
    return res;
}

export function can_make_edge_nonsmooth(eid: Edge) {
    const res = wrapped.can_make_edge_nonsmooth(from(eid));
    if (res < 0) debugstop();
    return res;
}

export function make_edge_smooth(eid: Edge, radius: number) {
    uil_handle_pre_core_action(null);

    user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
    const fs = uil_get_faces_of_edge(eid);
    user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
    user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));

    // We ignore the radius and call with 0
    const res = wrapped.make_edge_smooth(from(eid), 0);
    console_log('Called phi_make_edge_smooth with radius:', radius);
    if (!res) debugstop();

    uil_handle_post_core_action(null);
    return res;
}

export function make_edge_nonsmooth(eid: Edge) {
    uil_handle_pre_core_action(null);

    user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
    const fs = uil_get_faces_of_edge(eid);
    user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
    user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));

    const res = wrapped.make_edge_nonsmooth(from(eid));
    if (!res) {debugstop();}

    uil_handle_post_core_action(null);
    return res;
}

export function smoothen_vertices(vertex_ids: Array<Vertex>, radius: number) {
    uil_handle_pre_core_action(null);

    for (const vid of vertex_ids) {
        user_modified_objects_hash.setItem(vid, uid_vertices.getItem(vid));
        for (const eid of uil_get_edges_of_vertex(vid)) {
            user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
            const fs = uil_get_faces_of_edge(eid);
            user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
            user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
        }
    }

    const heapBytes = allocate_heapBytes_array(fromArray(vertex_ids), 'int');
    const res = wrapped.smoothen_vertices(vertex_ids.length, heapBytes.byteOffset, radius);
    if (!res) debugstop();

    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(null);
    return res;
}

export function remove_vertex_smoothness(vertex_ids: Array<Vertex>) {
    uil_handle_pre_core_action(null);
    for (const vid of vertex_ids) {
        user_modified_objects_hash.setItem(vid, uid_vertices.getItem(vid));
        for (const eid of uil_get_edges_of_vertex(vid)) {
            user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
            const fs = uil_get_faces_of_edge(eid);
            user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
            user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
        }
    }
    const heapBytes = allocate_heapBytes_array(fromArray(vertex_ids), 'int');
    const res = wrapped.remove_vertex_smoothness(vertex_ids.length, heapBytes.byteOffset);
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(null);
    return res;
}

export function smoothen_edge_wrt_face(eid: Edge, fid: Face, vid: Vertex) {
    uil_handle_pre_core_action(null);
    user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
    const fs = uil_get_faces_of_edge(eid);
    user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
    user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
    const res = wrapped.smoothen_edge_wrt_face(from(eid), from(fid), from(vid));
    if (!res) debugstop();
    uil_handle_post_core_action(null);
    return res;
}

export function inflate_face(fid: Face, nsteps: number) {
    uil_handle_pre_core_action(['phi_inflate']);
    user_modified_objects_hash.setItem(fid, uid_faces.getItem(fid));
    const res = wrapped.inflate_face(from(fid), nsteps);
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_inlate']);
    return res;
}

export function deflate_face(fid: Face, nsteps: number) {
    uil_handle_pre_core_action(['phi_deflate']);
    user_modified_objects_hash.setItem(fid, uid_faces.getItem(fid));
    const res = wrapped.deflate_face(from(fid), nsteps);
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_deflate']);
    return res;
}

export function make_edges_planar(edge_ids: Array<Edge>, base: Vector3, normal: Vector3) {
    uil_handle_pre_core_action(null);
    for (const eid of edge_ids) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
        const vs = uil_get_vertices_of_edge(eid);
        user_modified_objects_hash.setItem(vs[0], uid_vertices.getItem(vs[0]));
        user_modified_objects_hash.setItem(vs[1], uid_vertices.getItem(vs[1]));
    }
    const heapBytes = allocate_heapBytes_array(fromArray(edge_ids), 'int');
    const res = wrapped.make_edges_planar(
        edge_ids.length,
        heapBytes.byteOffset,
        base.x,
        base.y,
        base.z,
        normal.x,
        normal.y,
        normal.z,
    );
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(null);
    return res;
}

export function pop_face(fid: Face, is_in: boolean, dir_vec: Vector3, dist: number, pop_vector: Vector3) {
    uil_handle_pre_core_action(['phi_pop']);

    const faces = new Set(uid_faces.keys());
    const edges = new Set(uid_edges.keys());
    const vertices = new Set(uid_vertices.keys());

    const dummy3 = [0, 0, 0];

    const dir_vec_ptr = (dir_vec === null || dir_vec.length() === 0)
        ? NullPointer
        : allocate_vector(dir_vec);

    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes = get_heapbytes(heapBytes_bundle);

    const res = wrapped.pop_face(
        fid,
        is_in ? 1 : 0,
        dir_vec_ptr,
        dist,
        getPointer(heapBytes),
    );
    if (!res) {debugstop();}

    if (res) {
        const heapFloats = reallocate_view_and_get_array(heapBytes_bundle, 'Float32Array', 3);
        pop_vector.x = heapFloats[0]!;
        pop_vector.y = heapFloats[1]!;
        pop_vector.z = heapFloats[2]!;
    }
    free_heapBytes_array(heapBytes);

    uid_faces.keys()
        .filter(outside(faces))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_faces.getItem(x)));
    uid_edges.keys()
        .filter(outside(edges))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_edges.getItem(x)));
    uid_vertices.keys()
        .filter(outside(vertices))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_vertices.getItem(x)));

    uil_handle_post_core_action(['phi_pop']);
    return res;
}

export function pop_faces(face_ids: Array<Face>, is_in: boolean, dir_vec: Vector3, dist: number, pop_vector: Vector3) {
    uil_handle_pre_core_action(['phi_pop_many']);

    const faces = new Set(uid_faces.keys());
    const edges = new Set(uid_edges.keys());
    const vertices = new Set(uid_vertices.keys());

    const heapBytes = allocate_heapBytes_array(fromArray(face_ids), 'int');

    const dummy3 = [0, 0, 0];

    const dir_vec_ptr = (dir_vec === null || dir_vec.length() === 0)
        ? NullPointer
        : allocate_vector(dir_vec);

    const heapBytes_coords_bundle = allocate_heapBytes_array_bundle(dummy3, 'float');
    const heapBytes_coords = get_heapbytes(heapBytes_coords_bundle);

    const res = wrapped.pop_faces(
        face_ids.length,
        getPointer(heapBytes),
        is_in ? 1 : 0,
        dir_vec_ptr,
        dist,
        getPointer(heapBytes_coords),
    );
    if (!res) debugstop();

    if (res) {
        const heapFloats_coords = reallocate_view_and_get_array(heapBytes_coords_bundle, 'Float32Array', 3);
        pop_vector.x = heapFloats_coords[0]!;
        pop_vector.y = heapFloats_coords[1]!;
        pop_vector.z = heapFloats_coords[2]!;
    }

    free_heapBytes_array(heapBytes);
    free_heapBytes_array(heapBytes_coords);

    uid_faces.keys()
        .filter(outside(faces))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_faces.getItem(x)));
    uid_edges.keys()
        .filter(outside(edges))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_edges.getItem(x)));
    uid_vertices.keys()
        .filter(outside(vertices))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_vertices.getItem(x)));

    uil_handle_post_core_action(['phi_pop_many']);
    return res;
}

export function make_hole_or_handle(f1id: Face, f2id: Face, is_hole: boolean, v1id: Vertex, v2id: Vertex) {
    uil_handle_pre_core_action(['hole', 'handle']);

    const faces = new Set(uid_faces.keys());
    const edges = new Set(uid_edges.keys());
    const vertices = new Set(uid_vertices.keys());

    const res = wrapped.make_hole_or_handle(
        from(f1id),
        from(f2id),
        is_hole ? 1 : 0,
        from(v1id),
        from(v2id),
    );
    if (!res) debugstop();

    uid_faces.keys()
        .filter(outside(faces))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_faces.getItem(x)));
    uid_edges.keys()
        .filter(outside(edges))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_edges.getItem(x)));
    uid_vertices.keys()
        .filter(outside(vertices))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_vertices.getItem(x)));

    uil_handle_post_core_action(['hole', 'handle']);
    return res;
}

export function make_hole_or_handle_multi(
    f1ids: Array<Face>,
    f2ids: Array<Face>,
    is_hole: boolean,
    v1id: Vertex,
    v2id: Vertex,
) {
    uil_handle_pre_core_action(['hole', 'handle']);

    const faces = new Set(uid_faces.keys());
    const edges = new Set(uid_edges.keys());
    const vertices = new Set(uid_vertices.keys());

    const heapBytes1 = allocate_heapBytes_array(fromArray(f1ids), 'int');
    const heapBytes2 = allocate_heapBytes_array(fromArray(f2ids), 'int');
    const res = wrapped.make_hole_or_handle_multi(
        f1ids.length,
        heapBytes1.byteOffset,
        f2ids.length,
        heapBytes2.byteOffset,
        is_hole ? 1 : 0,
        from(v1id),
        from(v2id),
    );
    if (!res) debugstop();

    uid_faces.keys()
        .filter(outside(faces))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_faces.getItem(x)));
    uid_edges.keys()
        .filter(outside(edges))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_edges.getItem(x)));
    uid_vertices.keys()
        .filter(outside(vertices))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_vertices.getItem(x)));

    uil_handle_post_core_action(['hole', 'handle']);
    return res;
}

export function smoothen_edges_as_one(
    ids_of_edges_to_smoothen: Array<Edge>,
    strength: number,
    strictly_fix_end_ders: boolean,
    fix_end_ders_override = false,
) {
    strength = Math.max(strength, 0.01);

    uil_handle_pre_core_action(['phi_smoothen_edges_as_one']);
    for (const eid of ids_of_edges_to_smoothen) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
        const vs = uil_get_vertices_of_edge(eid);
        if (!uil_is_object_fixed(vs[0]))
            user_modified_objects_hash.setItem(vs[0], uid_vertices.getItem(vs[0]));
        if (!uil_is_object_fixed(vs[1]))
            user_modified_objects_hash.setItem(vs[1], uid_vertices.getItem(vs[1]));
    }

    const heapBytes = allocate_heapBytes_array(fromArray(ids_of_edges_to_smoothen), 'int');
    const res = wrapped.smoothen_edges_as_one(
        ids_of_edges_to_smoothen.length,
        heapBytes.byteOffset,
        strength,
        strictly_fix_end_ders ? 1 : 0,
        fix_end_ders_override ? 1 : 0,
    );

    if (!res) debugstop();

    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['phi_smoothen_edges_as_one']);
    return res;
}

export function can_smoothen_edges_as_one(ids_of_edges_to_smoothen: Array<Edge>) {
    const heapBytes = allocate_heapBytes_array(fromArray(ids_of_edges_to_smoothen), 'int');
    const res = wrapped.can_smoothen_edges_as_one(ids_of_edges_to_smoothen.length, heapBytes.byteOffset);
    if (res<0) debugstop();

    free_heapBytes_array(heapBytes);
    return res;
}

export function smoothen_chain_network(
    ids_of_edges_to_smoothen: Edge[],
    graph_distance: number,
    strength: number,
) {
    strength = Math.max(strength, 0.01);

    uil_handle_pre_core_action(['phi_smoothen_chain_network']);
    ids_of_edges_to_smoothen.forEach(function (eid){
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
        const vs = uil_get_vertices_of_edge(eid);
        if (!uil_is_object_fixed(vs[0]))
            user_modified_objects_hash.setItem(vs[0], uid_vertices.getItem(vs[0]));
        if (!uil_is_object_fixed(vs[1]))
            user_modified_objects_hash.setItem(vs[1], uid_vertices.getItem(vs[1]));
    });

    const heapBytes = allocate_heapBytes_array(fromArray(ids_of_edges_to_smoothen), 'int');

    const res = wrapped.smoothen_chain_network(
        ids_of_edges_to_smoothen.length,
        heapBytes.byteOffset,
        graph_distance,
        strength);

    if (!res) {debugstop();}

    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['phi_smoothen_chain_network']);
    return res;
}
/* */

export function smoothen_edges(
    ids_of_edges_to_smoothen: Edge[],
    strength: number,
    force_fix_1st_ders: boolean,
    force_fix_2nd_ders: boolean,
) {
    strength = Math.max(strength, 0.01);

    uil_handle_pre_core_action(['phi_smoothen_edges']);
    for (const eid of ids_of_edges_to_smoothen) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
    }
    const heapBytes = allocate_heapBytes_array(fromArray(ids_of_edges_to_smoothen), 'int');
    const res = wrapped.smoothen_edges(
        ids_of_edges_to_smoothen.length,
        heapBytes.byteOffset,
        strength,
        force_fix_1st_ders ? 1 : 0,
        force_fix_2nd_ders ? 1 : 0,
    );
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['phi_smoothen_edges']);
    return res;
}

export function make_edges_linear_as_one(ids_of_edges_to_make_linear: Edge[], force: boolean) {
    uil_handle_pre_core_action(null);
    for (const eid of ids_of_edges_to_make_linear) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
        const vs = uil_get_vertices_of_edge(eid);
        if (!uil_is_object_fixed(vs[0]))
            user_modified_objects_hash.setItem(vs[0], uid_vertices.getItem(vs[0]));
        if (!uil_is_object_fixed(vs[1]))
            user_modified_objects_hash.setItem(vs[1], uid_vertices.getItem(vs[1]));
    }
    const heapBytes = allocate_heapBytes_array(fromArray(ids_of_edges_to_make_linear), 'int');
    const res = wrapped.make_edges_linear_as_one(ids_of_edges_to_make_linear.length, heapBytes.byteOffset, force ? 1 : 0);
    if (!res) {debugstop();}
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(null);
    return res;
}

export function get_face_curvatures(fid: Face) {
    const res = wrapped.get_face_curvatures(from(fid));
    if (!res) debugstop();
    return res;
}

export function get_face_normals(fid: Face) {
    const res = wrapped.get_face_normals(from(fid));
    if (!res) debugstop();
    return res;
}

export function get_edge_num_of_face(fid: Face) {
    let ne = -1;
    const dummy1 = [0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_edge_num_of_face(from(fid), heapBytes.byteOffset);
    if (!ret) debugstop();
    if (ret) {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', 1);
        ne = heapInts[0]!;
    }
    free_heapBytes_array(heapBytes);
    return ne;
}

export function get_edge_num_of_vertex(vid: Vertex) {
    let ne = -1;
    const dummy1 = [0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_edge_num_of_vertex(from(vid), heapBytes.byteOffset);
    if (!ret) debugstop();
    if (ret) {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', 1);
        ne = heapInts[0]!;
    }
    free_heapBytes_array(heapBytes);
    return ne;
}

export function get_edges_of_face(fid: Face, ne: number) {
    if (ne <= 0) {
        console_log_xml('Error: Called jcore_get_edges_of_face with non-positive edge number!');
        return null;
    }
    const edge_ids: Edge[] = [];
    const dummy = new Array(ne);
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_edges_of_face(from(fid), ne, heapBytes.byteOffset);
    if (!ret) debugstop();
    if (ret) {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', ne);
        for (let i = 0; i < ne; i++)
            edge_ids.push(heapInts[i] as any as Edge);
    }
    free_heapBytes_array(heapBytes);
    return edge_ids;
}

export function get_edges_of_vertex(vid: Vertex, ne: number): Array<Edge> | null {
    if (ne <= 0) {
        console_log_xml('Error: Called jcore_get_edges_of_vertex with non-positive edge number!');
        return null;
    }
    const edge_ids: Array<Edge> = [];
    const dummy = new Array(ne);
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_edges_of_vertex(from(vid), ne, heapBytes.byteOffset);
    if (!ret) debugstop();
    if (ret) {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', ne);
        for (let i = 0; i < ne; i++)
            edge_ids.push(heapInts[i] as any as Edge);
    }
    free_heapBytes_array(heapBytes);
    return edge_ids;
}

export function get_faces_of_edge(eid: Edge): Array<Face> {
    const face_ids: Array<Face> = [];
    const dummy1 = [0];
    const heapBytes1_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes1 = get_heapbytes(heapBytes1_bundle);
    const heapBytes2_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes2 = get_heapbytes(heapBytes2_bundle);
    const ret = wrapped.get_faces_of_edge(from(eid), heapBytes1.byteOffset, heapBytes2.byteOffset);
    if (!ret) debugstop();
    if (ret) {
        const heapInts1 = reallocate_view_and_get_array(heapBytes1_bundle, 'Int32Array', 1);
        const heapInts2 = reallocate_view_and_get_array(heapBytes2_bundle, 'Int32Array', 1);
        face_ids.push(heapInts1[0] as any as Face);
        face_ids.push(heapInts2[0] as any as Face);
    }
    free_heapBytes_array(heapBytes1);
    free_heapBytes_array(heapBytes2);
    return face_ids;
}

export function get_vertices_of_edge(eid: Edge): Array<Vertex> {
    const vertex_ids: Array<Vertex> = [];
    const dummy1 = [0];
    const heapBytes1_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes1 = get_heapbytes(heapBytes1_bundle);
    const heapBytes2_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes2 = get_heapbytes(heapBytes2_bundle);
    const ret = wrapped.get_vertices_of_edge(from(eid), heapBytes1.byteOffset, heapBytes2.byteOffset);
    if (!ret) {debugstop();}
    if (ret) {
        const heapInts1 = reallocate_view_and_get_array(heapBytes1_bundle, 'Int32Array', 1);
        const heapInts2 = reallocate_view_and_get_array(heapBytes2_bundle, 'Int32Array', 1);
        vertex_ids.push(heapInts1[0] as any as Vertex);
        vertex_ids.push(heapInts2[0] as any as Vertex);
    }
    free_heapBytes_array(heapBytes1);
    free_heapBytes_array(heapBytes2);
    return vertex_ids;
}

export function get_body_of_face(fid: Face): Body {
    let bid = -1;
    const dummy1 = [0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_body_of_face(
        fid,
        getPointer(heapBytes),
    );
    if (!ret) debugstop();
    if (ret)  {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', 1);
        bid = heapInts[0]!;
    }
    free_heapBytes_array(heapBytes);
    return bid as any as Body;
}

export function get_body_of_edge(eid: Edge) {
    let bid = -1;
    const dummy1 = [0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_body_of_edge(
        eid,
        getPointer(heapBytes),
    );
    if (!ret) debugstop();
    if (ret)  {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', 1);
        bid = heapInts[0]!;
    }
    free_heapBytes_array(heapBytes);
    return bid;
}

export function get_body_of_vertex(vid: Vertex) {
    let bid = -1;
    const dummy1 = [0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_body_of_vertex(
        vid,
        getPointer(heapBytes),
    );
    if (!ret) debugstop();
    if (ret)  {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', 1);
        bid = heapInts[0]!;
    }
    free_heapBytes_array(heapBytes);
    return bid;
}

export function is_vertex_smooth(vid: Vertex) {
    const res = wrapped.is_vertex_smooth(from(vid));
    if (res < 0) debugstop();
    return res;
}

export function is_vertex_of_face(vid: Vertex, fid: Face) {
    const res = wrapped.is_vertex_of_face(from(vid), from(fid));
    if (res < 0) debugstop();
    return res;
}

export function is_joint_smooth(e1id: Edge, e2id: Edge) {
    const res = wrapped.is_joint_smooth(from(e1id), from(e2id));
    if (res < 0) {debugstop();}
    return res;
}

export function triangulate_faces(face_ids: Face[], triangles_per_face: number) {
    uil_handle_pre_core_action(null);
    const heapBytes = allocate_heapBytes_array(fromArray(face_ids), 'int');
    const res = wrapped.triangulate_faces(
        face_ids.length,
        heapBytes.byteOffset,
        triangles_per_face,
        wrapped.set_tri_f_ptr,
    );
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(null);
    return res;
}

export function set_display_face_triangulation_res(triangles_per_face: number) {
    uil_handle_pre_core_action(null);
    const res = wrapped.set_display_face_triangulation_res(triangles_per_face);
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_set_display_face_triangulation_res']);
    return res;
}

export function get_display_face_triangulation_res(): number {
    uil_handle_pre_core_action(null);
    const n: 0 | number = wrapped.get_display_face_triangulation_res();
    if (!n) debugstop();
    uil_handle_post_core_action(['phi_get_display_face_triangulation_res']);
    return n;
}

export function set_display_face_triangulation_res_for_drag(triangles_per_face: number) {
    uil_handle_pre_core_action(null);
    const res = wrapped.set_display_face_triangulation_res_for_drag(triangles_per_face);
    if (!res) debugstop();
    uil_handle_post_core_action(null);
    return res;
}

export function get_display_face_triangulation_res_for_drag() {
    uil_handle_pre_core_action(null);
    const n: 0 | number = wrapped.get_display_face_triangulation_res_for_drag();
    if (!n) debugstop();
    uil_handle_post_core_action(null);
    return n;
}

export function set_face_tessel(on: boolean) {
    //uil_handle_pre_core_action(null);
    const res = wrapped.set_face_tessel(on ? 1 : 0);
    if (!res) debugstop();
    return res;
}

export function set_global_fairing_strength(strength: number) {
    const res = wrapped.set_global_fairing_strength(strength);
    if (!res) debugstop();
    return res;
}

export function get_global_fairing_strength() {
    const res = wrapped.get_global_fairing_strength();
    if (!res) debugstop();
    return res;
}

export function get_vertex_normal(vid: Vertex) {
    const vec = new Vector3(0, 0, 0);
    const coords = [0, 0, 0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(coords, 'float');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_vertex_normal(from(vid), heapBytes.byteOffset);
    if (!ret) debugstop();
    if (ret) {
        const heapFloats = reallocate_view_and_get_array(heapBytes_bundle, 'Float32Array', 3);
        vec.x = heapFloats[0]!;
        vec.y = heapFloats[1]!;
        vec.z = heapFloats[2]!;
    }
    free_heapBytes_array(heapBytes);
    return vec;
}

/*
function logged_jcore_get_edge_end_tangents(eid) {
  var coords = [0,0,0];
  var heapBytes_v1_bundle = allocate_heapBytes_array_bundle(coords, 'float');
  var heapBytes_v1 = get_heapbytes(heapBytes_v1_bundle);
  var heapBytes_v2_bundle = allocate_heapBytes_array_bundle(coords, 'float');
  var heapBytes_v2 = get_heapbytes(heapBytes_v2_bundle);

  var r = null;
  var res = wrapped.get_edge_end_tangents(eid, heapBytes_v1.byteOffset, heapBytes_v2.byteOffset);
  if (!res) {debugstop();}
  if (res) {
    var heapFloats_v1 = reallocate_view_and_get_array(heapBytes_v1_bundle, 'Float32Array', 3);
    var heapFloats_v2 = reallocate_view_and_get_array(heapBytes_v2_bundle, 'Float32Array', 3);
    var vec1 = new Vector3(heapFloats_v1[0], heapFloats_v1[1], heapFloats_v1[2]);
    var vec2 = new Vector3(heapFloats_v2[0], heapFloats_v2[1], heapFloats_v2[2]);
    r = [vec1.normalize(), vec2.normalize()];
  }
  free_heapBytes_array(heapBytes_v1);
  free_heapBytes_array(heapBytes_v2);
  return r;
}
*/

export function get_point_at_edge_param(eid: Edge, u: number) {
    const vec = new Vector3(0, 0, 0);
    const coords = [0, 0, 0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(coords, 'float');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_point_at_edge_param(from(eid), u, heapBytes.byteOffset);
    if (!ret) debugstop();
    if (ret) {
        const heapFloats = reallocate_view_and_get_array(heapBytes_bundle, 'Float32Array', 3);
        vec.x = heapFloats[0]!;
        vec.y = heapFloats[1]!;
        vec.z = heapFloats[2]!;
    } else
        console_log_xml('ERROR in get_point_at_edge_param');
    free_heapBytes_array(heapBytes);
    return vec;
}

export function get_tangent_at_edge_param(eid: Edge, u: number) {
    // The vector to be returned
    const vec = new Vector3(0, 0, 0);
    const coords = [0, 0, 0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(coords, 'float');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_tangent_at_edge_param(from(eid), u, heapBytes.byteOffset);
    if (!ret) {debugstop();}
    if (ret) {
        const heapFloats = reallocate_view_and_get_array(heapBytes_bundle, 'Float32Array', 3);
        vec.x = heapFloats[0]!;
        vec.y = heapFloats[1]!;
        vec.z = heapFloats[2]!;
    } else
        console_log_xml('ERROR in get_tangent_at_edge_param');
    free_heapBytes_array(heapBytes);
    return vec;
}

export function move_vertex_to_coords(vertex_id: Vertex, x: number, y: number, z: number) {
    uil_handle_pre_core_action(null);

    user_modified_objects_hash.setItem(vertex_id, uid_vertices.getItem(vertex_id));
    for (const eid of uil_get_edges_of_vertex(vertex_id)) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
    }

    const res = wrapped.move_vertex_to_coords(from(vertex_id), x, y, z, uid_get_ball_size(), uid_get_ball_hardness());
    if (!res) debugstop();
    uil_handle_post_core_action(null);
    return res;
}

export function translate_faces_to_nurbs(face_ids: Face[]) {
    const heapBytes = allocate_heapBytes_array(fromArray(face_ids), 'int');
    const s = wrapped.translate_faces_to_nurbs(face_ids.length, heapBytes.byteOffset) as any as Pointer;
    const str = Module.UTF8ToString(s);
    free_heapBytes_array(heapBytes);
    return str;
}

export function load_model(str: string, into_current: boolean, additional_scale_factor: number, no_id_rehash: boolean) {
    uil_handle_pre_core_action(['no coords', 'clear face materials', 'load_model']);
    uid_current_api_call = 'phi_load_model';
    const strptr = Module._malloc(str.length * 4 + 1);
    Module.stringToUTF8(str, strptr, str.length * 4 + 1);

    let model_scaling_fac = 0;

    const dummy1 = [0];
    const heapBytes_extradata_string_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_extradata_string = get_heapbytes(heapBytes_extradata_string_bundle);
    const heapBytes_extradata_length_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_extradata_length = get_heapbytes(heapBytes_extradata_length_bundle);
    const heapBytes_munits_string_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_munits_string = get_heapbytes(heapBytes_munits_string_bundle);
    const heapBytes_model_scaling_fac_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_model_scaling_fac = get_heapbytes(heapBytes_model_scaling_fac_bundle);

    console_log_xml('load_model_str:'); // XXX
    console_log_xml(str); // XXX

    let return_array = null;
    const res = wrapped.load_model(
        strptr,
        str.length,
        into_current ? 1 : 0,
        additional_scale_factor,
        null,
        no_id_rehash ? 1 : 0,
        getPointer(heapBytes_extradata_string),
        getPointer(heapBytes_extradata_length),
        getPointer(heapBytes_model_scaling_fac),
        getPointer(heapBytes_munits_string),
    );

    if (res === 0) {
        console_log_xml('Failed to load model.');
        debugstop();
    } else {
        let extradata_str = '';
        if (use_phi_file_extra_data) {
            const heapInts_extradata_ptr = reallocate_view_and_get_array(heapBytes_extradata_string_bundle, 'Int32Array', 1);
            const heapInts_extradata_length = reallocate_view_and_get_array(heapBytes_extradata_length_bundle, 'Int32Array', 1);
            const extradata_len = heapInts_extradata_length[0];
            const heapBytes_extradata = new Uint8Array(Module.HEAPU8.buffer, heapInts_extradata_ptr as any as number, extradata_len);
            extradata_str = new TextDecoder('utf-8').decode(heapBytes_extradata);
            const heapFloats_model_scaling_fac = reallocate_view_and_get_array(heapBytes_model_scaling_fac_bundle, 'Float32Array', 1);
            model_scaling_fac = heapFloats_model_scaling_fac[0]!;
        }
        return_array = [model_scaling_fac, extradata_str] as [number, string];
    }
    uid_current_api_call = null;
    free_heapBytes_array(heapBytes_extradata_string);
    free_heapBytes_array(heapBytes_extradata_length);
    free_heapBytes_array(heapBytes_munits_string);
    free_heapBytes_array(heapBytes_model_scaling_fac);
    Module._free(strptr);
    uil_handle_post_core_action(['load_model']);
    return return_array;
}

export function get_world_scaling_factor() {
    const res: number = wrapped.get_world_scaling_factor();
    return res;
}

export function set_world_scaling_factor(factor: number) {
    uil_handle_pre_core_action(null);
    const res = wrapped.set_world_scaling_factor(factor);
    if (!res) {debugstop();}
    uil_handle_post_core_action(null);
    return res;
}

export function set_add_edge_pair_tangencies_on_load(on: 1 | 0 | boolean) {
    uil_handle_pre_core_action(null);
    const res = wrapped.set_add_edge_pair_tangencies_on_load(on ? 1 : 0);
    if (!res) {debugstop();}
    uil_handle_post_core_action(null);
    return res;
}

export function get_num_of_model_bodies(): number {
    let nbodies = -1;
    const dummy = [0];
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_body_num_of_model(heapBytes.byteOffset);
    if (!ret) {debugstop();}

    if (ret) {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', 1);
        nbodies = heapInts[0]!;
        if (nbodies < 1)
            console_log_xml('Error: jcore_get_body_num_of_model returned less than 1 body!');
    } else {
        console_log_xml('ERROR in phi_get_body_num_of_model');
    }
    free_heapBytes_array(heapBytes);
    return nbodies;
}

export function get_model_bodies(nbodies: number): Array<Body> {
    const body_ids: Array<Body> = [];
    const dummy = new Array(nbodies);
    const heapBytes_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes = get_heapbytes(heapBytes_bundle);
    const ret = wrapped.get_bodies_of_model(nbodies, heapBytes.byteOffset);
    if (!ret) debugstop();

    if (ret) {
        const heapInts = reallocate_view_and_get_array(heapBytes_bundle, 'Int32Array', nbodies);
        for (let i = 0; i < nbodies; i++)
            body_ids.push(heapInts[i] as any as Body);
    }
    free_heapBytes_array(heapBytes);
    return body_ids;
}

export function is_body_open(bid: Body) {
    return wrapped.is_body_open(from(bid));
}

export function save_model(extra_data_str: string, scale_fac: number): string {
    uil_handle_pre_core_action(['save_model']);
    if (!use_phi_file_extra_data) extra_data_str = '';
    const extra_data_strptr = Module._malloc(extra_data_str.length * 4 + 1);
    Module.stringToUTF8(extra_data_str, extra_data_strptr, extra_data_str.length+1);
    const s = wrapped.save_model(extra_data_strptr, extra_data_str.length, scale_fac, null) as any as Pointer;
    const str = Module.UTF8ToString(s);
    Module._free(extra_data_strptr);
    uil_handle_post_core_action(['save_model']);
    return str;
}

export function undo() {
    uil_handle_pre_core_action(['phi_undo']);
    const res = wrapped.undo();
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_undo']);
    return res;
}

export function redo() {
    uil_handle_pre_core_action(['phi_redo']);
    const res = wrapped.redo();
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_redo']);
    return res;
}

export function can_undo() {
    const res = wrapped.can_undo();
    if (res < 0) debugstop();
    return res;
}

export function can_redo() {
    const res = wrapped.can_redo();
    if (res < 0) debugstop();
    return res;
}

export function can_inflate(fid: Face) {
    const res = wrapped.can_inflate_face(from(fid));
    if (res < 0) debugstop();
    return res;
}

export function can_deflate(fid: Face) {
    const res = wrapped.can_deflate_face(from(fid));
    if (res < 0) debugstop();
    return res;
}

export function is_edge_linear(eid: Edge) {
    const res = wrapped.is_edge_linear(from(eid));
    if (res < 0) debugstop();
    return res;
}

export function is_edge_linear_geometry(eid: Edge) {
    const res = wrapped.is_edge_linear_geometry(from(eid));
    if (res < 0) debugstop();
    return res;
}

export function is_object_fixed(id: Vertex | Edge | Face) {
    const res = wrapped.is_object_fixed(from(id));
    if (res < 0) debugstop();
    return res;
}

export function change_object_fixedness(ids: Array<Edge | Vertex | Face>, fixed: boolean) {
    uil_handle_pre_core_action(['phi_change_object_fixedness']);
    const heapBytes = allocate_heapBytes_array(fromArray(ids), 'int');
    const res = wrapped.change_object_fixedness(ids.length, heapBytes.byteOffset, fixed ? 1 : 0);
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['phi_change_object_fixedness']);
    return res;
}

export function is_object_visible(id: Edge | Vertex | Face) {
    return wrapped.is_object_visible(from(id));
}

export function change_object_visibility(ids: Array<Vertex | Edge | Face>, visible: boolean) {
    uil_handle_pre_core_action(['phi_change_object_visibility']);
    const heapBytes = allocate_heapBytes_array(fromArray(ids), 'int');
    const res = wrapped.change_object_visibility(ids.length, heapBytes.byteOffset, visible ? 1 : 0);
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['phi_change_object_visibility']);
    return res;
}

export function set_linearity_of_edges(ids_of_edges: Edge[], make_linear: boolean, force: boolean) {
    uil_handle_pre_core_action(['phi_make_edges_linear']);
    for (const eid of ids_of_edges) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
    }
    const heapBytes = allocate_heapBytes_array(fromArray(ids_of_edges), 'int');
    const res = make_linear
        ? wrapped.make_edges_linear(ids_of_edges.length, heapBytes.byteOffset, force ? 1 : 0)
        : wrapped.make_edges_nonlinear(ids_of_edges.length, heapBytes.byteOffset);
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    uil_handle_post_core_action(['phi_make_edges_linear']);
    return res;
}

export function is_vertex_of_edge(vertex_id: Vertex, edge_id: Edge) {
    const res = wrapped.is_vertex_of_edge(from(vertex_id), from(edge_id));
    if (res < 0) debugstop();
    return res;
}

export function can_make_edges_circular(edge_ids: Edge[], vertex_ids: Vertex[], center_vertex_id: Vertex) {
    const heapEdgeBytes = allocate_heapBytes_array(fromArray(edge_ids), 'int');
    const heapVertexBytes = allocate_heapBytes_array(fromArray(vertex_ids), 'int');
    const res = wrapped.can_make_edges_circular(
        edge_ids.length,
        heapEdgeBytes.byteOffset,
        vertex_ids.length,
        heapVertexBytes.byteOffset,
        from(center_vertex_id),
    );
    if (res < 0) debugstop();
    free_heapBytes_array(heapEdgeBytes);
    free_heapBytes_array(heapVertexBytes);
    return res;
}

export function make_edges_circular(edge_ids: Edge[], vertex_ids: Vertex[], center_vertex_id: Vertex) {
    uil_handle_pre_core_action(['phi_make_edges_circular']);

    for (const eid of edge_ids) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
        const vs = uil_get_vertices_of_edge(eid);
        user_modified_objects_hash.setItem(vs[0], uid_vertices.getItem(vs[0]));
        user_modified_objects_hash.setItem(vs[1], uid_vertices.getItem(vs[1]));
    }

    const heapEdgeBytes = allocate_heapBytes_array(fromArray(edge_ids), 'int');
    const heapVertexBytes = allocate_heapBytes_array(fromArray(vertex_ids), 'int');
    const res = wrapped.make_edges_circular(
        edge_ids.length,
        heapEdgeBytes.byteOffset,
        vertex_ids.length,
        heapVertexBytes.byteOffset,
        from(center_vertex_id),
    );
    if (!res) debugstop();
    free_heapBytes_array(heapEdgeBytes);
    free_heapBytes_array(heapVertexBytes);
    uil_handle_post_core_action(['phi_make_edges_circular']);
    return res;
}

export function calc_ray_dir_for_face_sampling(f_id: Face, v0_id: Vertex, v1_id: Vertex, linedir: Vector3) {
    const dummy = [0];
    const heapBytes_px_bundle = allocate_heapBytes_array_bundle(dummy, 'float');
    const heapBytes_px = get_heapbytes(heapBytes_px_bundle);
    const heapBytes_py_bundle = allocate_heapBytes_array_bundle(dummy, 'float');
    const heapBytes_py = get_heapbytes(heapBytes_py_bundle);
    const heapBytes_pz_bundle = allocate_heapBytes_array_bundle(dummy, 'float');
    const heapBytes_pz = get_heapbytes(heapBytes_pz_bundle);

    const res = wrapped.calc_ray_dir_for_face_sampling(from(f_id), from(v0_id), from(v1_id), heapBytes_px.byteOffset, heapBytes_py.byteOffset, heapBytes_pz.byteOffset);
    if (!res) debugstop();
    if (res) {
        const heapFloats_px = reallocate_view_and_get_array(heapBytes_px_bundle, 'Float32Array', 1);
        const heapFloats_py = reallocate_view_and_get_array(heapBytes_py_bundle, 'Float32Array', 1);
        const heapFloats_pz = reallocate_view_and_get_array(heapBytes_pz_bundle, 'Float32Array', 1);
        linedir.x = heapFloats_px[0]!;
        linedir.y = heapFloats_py[0]!;
        linedir.z = heapFloats_pz[0]!;
    }
    free_heapBytes_array(heapBytes_px);
    free_heapBytes_array(heapBytes_py);
    free_heapBytes_array(heapBytes_pz);
    return res;
}

export function can_make_edges_symmetric(e1id: Edge, e2id: Edge, b: Vector3, n: Vector3) {
    const res = wrapped.can_make_edges_symmetric(from(e1id), from(e2id), b.x, b.y, b.z, n.x, n.y, n.z);
    return res;
}

export function make_edges_symmetric(e1id: Edge, e2id: Edge, b: Vector3, n: Vector3, dont_affect_modified_objects = false) {
    if (dont_affect_modified_objects)
        uil_handle_pre_core_action(['phi_make_edges_symmetric', 'dont_affect_modified_objects']);
    else
        uil_handle_pre_core_action(['phi_make_edges_symmetric']);
    user_modified_objects_hash.setItem(e1id, uid_edges.getItem(e1id));
    user_modified_objects_hash.setItem(e2id, uid_edges.getItem(e2id));

    const res = wrapped.make_edges_symmetric(from(e1id), from(e2id), b.x, b.y, b.z, n.x, n.y, n.z);
    if (!res) debugstop();
    if (dont_affect_modified_objects)
        uil_handle_post_core_action(['phi_make_edges_symmetric', 'dont_affect_modified_objects']);
    else
        uil_handle_post_core_action(['phi_make_edges_symmetric']);
    return res;
}

export function set_vertex_distance(v1id: Vertex, v2id: Vertex, dist: number) {
    uil_handle_pre_core_action(['phi_set_vertex_distance']);
    user_modified_objects_hash.setItem(v1id, uid_vertices.getItem(v1id));
    user_modified_objects_hash.setItem(v2id, uid_vertices.getItem(v2id));
    for (const eid of uil_get_edges_of_vertex(v2id)) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
    }
    const res = wrapped.set_vertex_distance(from(v1id), from(v2id), dist);
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_set_vertex_distance']);
    return res;
}

export function can_make_face_circular(fid: Face) {
    const res = wrapped.can_make_face_circular(from(fid));
    if (res < 0) debugstop();
    return res;
}

export function make_face_circular(fid: Face) {
    uil_handle_pre_core_action(['phi_make_face_circular']);
    user_modified_objects_hash.setItem(fid, uid_faces.getItem(fid));
    for (const eid of uil_get_edges_of_face(fid)) {
        user_modified_objects_hash.setItem(eid, uid_edges.getItem(eid));
        const fs = uil_get_faces_of_edge(eid);
        user_modified_objects_hash.setItem(fs[0], uid_faces.getItem(fs[0]));
        user_modified_objects_hash.setItem(fs[1], uid_faces.getItem(fs[1]));
        const vs = uil_get_vertices_of_edge(eid);
        user_modified_objects_hash.setItem(vs[0], uid_vertices.getItem(vs[0]));
        user_modified_objects_hash.setItem(vs[1], uid_vertices.getItem(vs[1]));
    }
    const res = wrapped.make_face_circular(from(fid));
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_make_face_circular']);
    return res;
}

export function get_edge_freedom(eid: Edge) {
    const res: number = wrapped.get_edge_curve_freedom(from(eid));
    if (!res) debugstop();
    return res;
}

export function get_edge_length(eid: Edge) {
    let length = -1;
    const dummy1 = [0];
    const heapBytes_length_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_length = get_heapbytes(heapBytes_length_bundle);
    const res = wrapped.get_edge_length(
        eid,
        getPointer(heapBytes_length),
    );
    if (!res) debugstop();
    if (res) {
        const heapFloats_length = reallocate_view_and_get_array(heapBytes_length_bundle, 'Float32Array', 1);
        length = heapFloats_length[0]!;
    }
    free_heapBytes_array(heapBytes_length);
    return length;
}

export function set_edge_freedoms(edge_ids: Edge[], freedom: number) {
    const heapBytes = allocate_heapBytes_array(fromArray(edge_ids), 'int');
    const res = wrapped.set_curve_freedom_of_edges(edge_ids.length, heapBytes.byteOffset, freedom);
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    return res;
}

export function set_body_label(body_ids: Body[], label_str: string) {
    const heapBytes = allocate_heapBytes_array(fromArray(body_ids), 'int');
    const strptr = Module._malloc(label_str.length * 4 + 1);
    Module.stringToUTF8(label_str, strptr, label_str.length * 4 + 1);
    const res = wrapped.set_body_label(body_ids.length, heapBytes.byteOffset, from(strptr), label_str.length);
    if (!res) {debugstop();}
    free_heapBytes_array(heapBytes);
    Module._free(strptr);
    return res;
}

export function get_body_label(body_id: Body): null | string {
    const s = wrapped.get_body_label(from(body_id)) as any as Pointer;
    let str = null as null | string;
    if (s != null)
        str = Module.UTF8ToString(s);
    return str;
}

export function set_face_label(face_ids: Face[], label_str: string) {
    const heapBytes = allocate_heapBytes_array(fromArray(face_ids), 'int');
    const strptr = Module._malloc(label_str.length * 4 + 1);
    Module.stringToUTF8(label_str, strptr, label_str.length * 4 + 1);
    const res = wrapped.set_face_label(face_ids.length, heapBytes.byteOffset, from(strptr), label_str.length);
    if (!res) {debugstop();}
    free_heapBytes_array(heapBytes);
    Module._free(strptr);
    return res;
}

export function get_face_label(face_id: Face): null | string {
    const s = wrapped.get_face_label(from(face_id)) as any as Pointer;
    let str = null as null | string;
    if (s != null)
        str = Module.UTF8ToString(s);
    return str;
}

export function set_face_material(face_ids: Face[], material_str: string) {
    if (face_ids.length == 0)
        return 0;
    const heapBytes = allocate_heapBytes_array(fromArray(face_ids), 'int');
    const strptr = Module._malloc(material_str.length * 4 + 1);
    Module.stringToUTF8(material_str, strptr, material_str.length * 4 + 1);
    const res = wrapped.set_face_material(face_ids.length, heapBytes.byteOffset, from(strptr));
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    Module._free(strptr);
    return res;
}

export function get_face_material(face_id: Face) {
    const s = wrapped.get_face_material(from(face_id)) as any as Pointer;
    let str = null as null | string;
    if (s != null)
        str = Module.UTF8ToString(s);
    return str;
}

export function set_face_color(face_ids: Face[], r: number, g: number, b: number) {
    if (face_ids.length == 0)
        return 0;
    const heapBytes = allocate_heapBytes_array(fromArray(face_ids), 'int');
    const res = wrapped.set_face_colour(face_ids.length, heapBytes.byteOffset, r, g, b);
    if (!res) debugstop();
    free_heapBytes_array(heapBytes);
    return res;
}

export function get_face_color(face_id: Face, wv_r: WrappedNumber, wv_g: WrappedNumber, wv_b: WrappedNumber) {
    const dummy = [0];
    const heapBytes_r_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes_r = get_heapbytes(heapBytes_r_bundle);
    const heapBytes_g_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes_g = get_heapbytes(heapBytes_g_bundle);
    const heapBytes_b_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes_b = get_heapbytes(heapBytes_b_bundle);
    const res = wrapped.get_face_colour(from(face_id), heapBytes_r.byteOffset, heapBytes_g.byteOffset, heapBytes_b.byteOffset);

    if (!res) debugstop();
    if (res) {
        const heapFloats_r = reallocate_view_and_get_array(heapBytes_r_bundle, 'Int32Array', 1);
        const heapFloats_g = reallocate_view_and_get_array(heapBytes_g_bundle, 'Int32Array', 1);
        const heapFloats_b = reallocate_view_and_get_array(heapBytes_b_bundle, 'Int32Array', 1);
        // Copy results to JS structures
        wv_r.v = heapFloats_r[0]!;
        wv_g.v = heapFloats_g[0]!;
        wv_b.v = heapFloats_b[0]!;
    }

    free_heapBytes_array(heapBytes_r);
    free_heapBytes_array(heapBytes_g);
    free_heapBytes_array(heapBytes_b);
    return res;
}

export function get_edge_curvatures(edge_id: Edge, no_of_samples: number): [ Vector3[], Vector3[] ] | null {
    const sample_p_coords = [];
    const sample_vec_coords = [];
    let return_tuple = null;

    const dummy_p = new Array(no_of_samples*3);
    const heapBytes_p_bundle = allocate_heapBytes_array_bundle(dummy_p, 'float');
    const heapBytes_p = get_heapbytes(heapBytes_p_bundle);
    const dummy_vec = new Array(no_of_samples*3);
    const heapBytes_vec_bundle = allocate_heapBytes_array_bundle(dummy_vec, 'float');
    const heapBytes_vec = get_heapbytes(heapBytes_vec_bundle);

    const ret = wrapped.get_edge_curvatures(from(edge_id), no_of_samples, heapBytes_p.byteOffset, heapBytes_vec.byteOffset);
    if (ret) {
        const heapFloats_p = reallocate_view_and_get_array(heapBytes_p_bundle, 'Float32Array', no_of_samples*3);
        const heapFloats_vec = reallocate_view_and_get_array(heapBytes_vec_bundle, 'Float32Array', no_of_samples*3);
        for (let i = 0; i < no_of_samples; i++)  {
            sample_p_coords.push(new Vector3(
                heapFloats_p[i*3],
                heapFloats_p[i*3+1],
                heapFloats_p[i*3+2])
            );
            sample_vec_coords.push(new Vector3(
                heapFloats_vec[i*3],
                heapFloats_vec[i*3+1],
                heapFloats_vec[i*3+2])
            );
        }
        return_tuple = [sample_p_coords, sample_vec_coords] as [Vector3[], Vector3[]];
    }
    free_heapBytes_array(heapBytes_p);
    free_heapBytes_array(heapBytes_vec);
    return return_tuple;
}

export function merge_bodies_on_edge(
    b1id: Body,
    b2id: Body,
    e1id: Edge,
    e2id: Edge,
    v1id: Vertex,
    v2id: Vertex,
) {
    uil_handle_pre_core_action(['phi_merge_bodies_on_edge']);

    const faces = new Set(uid_faces.keys());
    const edges = new Set(uid_edges.keys());
    const vertices = new Set(uid_vertices.keys());

    const res = wrapped.merge_bodies_on_edge(from(b1id), from(b2id), from(e1id), from(e2id), from(v1id), from(v2id));
    if (!res) debugstop();

    uid_faces.keys()
        .filter(outside(faces))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_faces.getItem(x)));
    uid_edges.keys()
        .filter(outside(edges))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_edges.getItem(x)));
    uid_vertices.keys()
        .filter(outside(vertices))
        .forEach(x => user_modified_objects_hash.setItem(x, uid_vertices.getItem(x)));

    uil_handle_post_core_action(['phi_merge_bodies_on_edge']);
    return res;
}

export function flip_body_inside_out(bid: Body) {
    uil_handle_pre_core_action(['phi_flip_body_inside_out']);

    uid_faces.keys()
        .filter(x => get_body_of_face(x) == bid)
        .forEach(x => user_modified_objects_hash.setItem(x, uid_faces.getItem(x)));

    uid_edges.keys()
        .filter(x => uil_get_body_of_edge(x) == bid)
        .forEach(x => user_modified_objects_hash.setItem(x, uid_edges.getItem(x)));

    uid_vertices.keys()
        .filter(x => uil_get_body_of_vertex(x) == bid)
        .forEach(x => user_modified_objects_hash.setItem(x, uid_vertices.getItem(x)));

    const res = wrapped.flip_body_inside_out(from(bid));

    uil_handle_post_core_action(['phi_flip_body_inside_out']);
    return res;
}

export function get_face_triangulation_info(face_id: Face, wv_ncoords: WrappedNumber, wv_ntriangles: WrappedNumber) {
    const dummy = [0];
    const heapBytes_ncoords_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes_ncoords = get_heapbytes(heapBytes_ncoords_bundle);
    const heapBytes_ntriangles_bundle = allocate_heapBytes_array_bundle(dummy, 'int');
    const heapBytes_ntriangles = get_heapbytes(heapBytes_ntriangles_bundle);

    const res = wrapped.get_face_triangulation_info(from(face_id), heapBytes_ncoords.byteOffset, heapBytes_ntriangles.byteOffset);
    if (!res) debugstop();
    if (res) {
        const heapInts_ncoords = reallocate_view_and_get_array(heapBytes_ncoords_bundle, 'Int32Array', 1);
        const heapInts_ntriangles = reallocate_view_and_get_array(heapBytes_ntriangles_bundle, 'Int32Array', 1);
        // Copy results to JS structures
        wv_ncoords.v = heapInts_ncoords[0]!;
        wv_ntriangles.v = heapInts_ntriangles[0]!;
    }
    free_heapBytes_array(heapBytes_ncoords);
    free_heapBytes_array(heapBytes_ntriangles);
    return res;
}

export function get_face_triangulation(
    face_id: Face,
    ncoords: number,
    ntriangles: number,
    coords: number[],
    triangles: number[],
) {
    const coords_array = new Array(ncoords*3);
    const triangles_array = new Array(ntriangles*3);
    const heapBytes_coords_bundle = allocate_heapBytes_array_bundle(coords_array, 'float');
    const heapBytes_coords = get_heapbytes(heapBytes_coords_bundle);
    const heapBytes_triangles_bundle = allocate_heapBytes_array_bundle(triangles_array, 'float');
    const heapBytes_triangles = get_heapbytes(heapBytes_triangles_bundle);
    const res = wrapped.get_face_triangulation(from(face_id), ncoords, ntriangles, heapBytes_coords.byteOffset, heapBytes_triangles.byteOffset);
    if (!res) debugstop();
    if (res) {
        const heapFloat_coords = reallocate_view_and_get_array(heapBytes_coords_bundle, 'Float32Array', ncoords * 3);
        const heapFloat_triangles = reallocate_view_and_get_array(heapBytes_triangles_bundle, 'Float32Array', ntriangles * 3);
        // Copy results to JS structures
        for (let i = 0; i < ncoords * 3; i++)
            coords.push(heapFloat_coords[i]!);
        for (let i = 0; i < ntriangles * 3; i++)
            triangles.push(heapFloat_triangles[i]!);
    }
    free_heapBytes_array(heapBytes_coords);
    free_heapBytes_array(heapBytes_triangles);
    return res;
}

export function get_edge_planes_from_point_intersection(
    edge_id: Edge,
    vertex_coords: Vector3,
    is_high_res: boolean,
    wv_tx: WrappedNumber,
    wv_ty: WrappedNumber,
    wv_tz: WrappedNumber,
) {
    const dummy1 = [0];
    const heapBytes_tx_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_ty_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_tz_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_tx = get_heapbytes(heapBytes_tx_bundle);
    const heapBytes_ty = get_heapbytes(heapBytes_ty_bundle);
    const heapBytes_tz = get_heapbytes(heapBytes_tz_bundle);

    const res = wrapped.get_edge_planes_from_point_intersection(
        from(edge_id),
        vertex_coords.x,
        vertex_coords.y,
        vertex_coords.z,
        is_high_res ? 1 : 0,
        heapBytes_tx.byteOffset,
        heapBytes_ty.byteOffset,
        heapBytes_tz.byteOffset,
    );

    if (!res) debugstop();
    if (res) {
        const heapFloats_tx = reallocate_view_and_get_array(heapBytes_tx_bundle, 'Float32Array', 1);
        const heapFloats_ty = reallocate_view_and_get_array(heapBytes_ty_bundle, 'Float32Array', 1);
        const heapFloats_tz = reallocate_view_and_get_array(heapBytes_tz_bundle, 'Float32Array', 1);
        wv_tx.v = heapFloats_tx[0]!;
        wv_ty.v = heapFloats_ty[0]!;
        wv_tz.v = heapFloats_tz[0]!;
    }

    free_heapBytes_array(heapBytes_tx);
    free_heapBytes_array(heapBytes_ty);
    free_heapBytes_array(heapBytes_tz);
    return res;
}

export function set_edge_tangent_at_vertex(edge_id: Edge, vertex_id: Vertex, tan: Vector3) {
    uil_handle_pre_core_action(['phi_set_edge_tangent_at_vertex']);
    user_modified_objects_hash.setItem(edge_id, uid_edges.getItem(edge_id));
    const res = wrapped.set_edge_tangent_at_vertex(from(edge_id), from(vertex_id), tan.x, tan.y, tan.z);
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_set_edge_tangent_at_vertex']);
    return res;
}

export function get_edge_tangent_at_vertex(edge_id: Edge, vertex_id: Vertex, tan: Vector3) {
    const dummy1 = [0];
    const heapBytes_tx_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_ty_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_tz_bundle = allocate_heapBytes_array_bundle(dummy1, 'float');
    const heapBytes_tx = get_heapbytes(heapBytes_tx_bundle);
    const heapBytes_ty = get_heapbytes(heapBytes_ty_bundle);
    const heapBytes_tz = get_heapbytes(heapBytes_tz_bundle);

    const res = wrapped.get_edge_tangent_at_vertex(
        from(edge_id),
        from(vertex_id),
        heapBytes_tx.byteOffset,
        heapBytes_ty.byteOffset,
        heapBytes_tz.byteOffset,
    );
    if (!res) debugstop();
    if (res) {
        const heapFloats_tx = reallocate_view_and_get_array(heapBytes_tx_bundle, 'Float32Array', 1);
        const heapFloats_ty = reallocate_view_and_get_array(heapBytes_ty_bundle, 'Float32Array', 1);
        const heapFloats_tz = reallocate_view_and_get_array(heapBytes_tz_bundle, 'Float32Array', 1);
        tan.x = heapFloats_tx[0]!;
        tan.y = heapFloats_ty[0]!;
        tan.z = heapFloats_tz[0]!;
    }
    free_heapBytes_array(heapBytes_tx);
    free_heapBytes_array(heapBytes_ty);
    free_heapBytes_array(heapBytes_tz);
    return res;
}

export function set_edge_fixing_of_tangent_at_vertex(edge_id: Edge, vertex_id: Vertex, must_fix: boolean) {
    uil_handle_pre_core_action(['phi_set_edge_fixing_of_tangent_at_vertex']);
    const res = wrapped.set_edge_fixing_of_tangent_at_vertex(from(edge_id), from(vertex_id), must_fix ? 1 : 0);
    if (!res) debugstop();
    uil_handle_post_core_action(['phi_set_edge_fixing_of_tangent_at_vertex']);
    return res;
}

export function get_edge_fixing_of_tangent_at_vertex(edge_id: Edge, vertex_id: Vertex, wv_is_fixed: WrappedValue<0 | 1>) {
    const dummy1 = [0];
    const heapBytes_is_fixed_bundle = allocate_heapBytes_array_bundle(dummy1, 'int');
    const heapBytes_is_fixed = get_heapbytes(heapBytes_is_fixed_bundle);

    const res = wrapped.get_edge_fixing_of_tangent_at_vertex(from(edge_id), from(vertex_id), heapBytes_is_fixed.byteOffset);
    if (!res) debugstop();

    if (res) {
        const heapInts_is_fixed = reallocate_view_and_get_array(heapBytes_is_fixed_bundle, 'Int32Array', 1);
        wv_is_fixed.v = heapInts_is_fixed[0] as 0 | 1;
    }
    free_heapBytes_array(heapBytes_is_fixed);
    return res;
}

export function stop_undo_tracking() {
    return wrapped.set_undo_tracking(0);
}

export function start_undo_tracking() {
    return wrapped.set_undo_tracking(1);
}

export function save_undo_state() {
    return wrapped.save_undo_state();
}

export function start_compound_operation() {
    return wrapped.start_compound_operation();
}

export function end_compound_operation() {
    return wrapped.end_compound_operation();
}

export function set_save_model_compression_level(clevel: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
    wrapped.set_save_model_compression_level(clevel);
}

export function copy_body(id: Body) {
    uil_handle_pre_core_action(null);
    const copied_body_id = wrapped.copy_body(from(id)) as Body | 0;
    if (copied_body_id === 0) debugstop();

    uid_faces.keys()
        .filter(x => uil_get_body_of_face(x) == copied_body_id)
        .forEach(x => user_modified_objects_hash.setItem(x, uid_faces.getItem(x)));

    uid_edges.keys()
        .filter(x => uil_get_body_of_edge(x) == copied_body_id)
        .forEach(x => user_modified_objects_hash.setItem(x, uid_edges.getItem(x)));

    uid_vertices.keys()
        .filter(x => uil_get_body_of_vertex(x) == copied_body_id)
        .forEach(x => user_modified_objects_hash.setItem(x, uid_vertices.getItem(x)));

    uil_handle_post_core_action(null);
    return copied_body_id;
}

export function set_auto_add_tangencies(on: boolean) {
    return wrapped.set_auto_add_tangencies(on ? 1 : 0);
}

export function debugstop(msg = '') {
    //console.log('Null or bad API response.' + msg);
    console_log('Null or bad API response.' + msg);
}

export function get_core_version_info(): string {
    const s = wrapped.get_core_version_info() as any as Pointer;
    const str = Module.UTF8ToString(s);
    Module._free(s);
    return str;
}

export function is_joint_geom_smooth(e1id: Edge, e2id: Edge) {
    const res = wrapped.is_joint_geom_smooth(from(e1id), from(e2id));
    if (res < 0) debugstop();
    return res;
}
